import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  VStack,
  useColorModeValue,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useToast,
  Spinner,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  HStack,
  Badge,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Checkbox,
  Select,
  FormHelperText,
  TableContainer,
  Flex,
  IconButton,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const scripts = {
  1: { 
    title: 'Importar Archivos', 
    description: 'Import and process data files',
    hasFileUpload: true 
  },
  2: { 
    title: 'Filtro Edad', 
    description: 'Filter data by age',
    hasAgeFilter: true
  },
  3: { 
    title: 'Normalización de Variables', 
    description: 'Normalize variables in the dataset',
    hasNormalization: true
  },
  4: { 
    title: 'Eliminar registros y mover columnas', 
    description: 'Remove records and rearrange columns',
    hasColumnManagement: true
  },
  5: { 
    title: 'Capitalizar nombre/apellido y separar apellidos', 
    description: 'Capitalize names and split last names',
    hasNameProcessing: true
  },
  6: { 
    title: 'Exclusión de nombres con 3 letras con excepciones', 
    description: 'Exclude 3-letter names with exceptions',
    hasNameExclusion: true
  },
  7: { 
    title: 'Límite de registros por valores', 
    description: 'Limit records by values in a specific column',
    hasRecordLimiting: true
  },
  '7.2': { 
    title: 'Selección de volumen específico por CP (sin filtros)', 
    description: 'Select specific volume by postal code',
    hasVolumeSelection: true,
    volumeSelectionType: 'cp'
  },
  '7.3': { 
    title: 'Selección de volumen específico por CP y edad (sin filtros)', 
    description: 'Select specific volume by postal code and age',
    hasVolumeSelection: true,
    volumeSelectionType: 'cp_and_age'
  },
  '7.4': { 
    title: 'Selección de volumen específico por CP principal y secundarios', 
    description: 'Select specific volume by primary and secondary postal codes',
    hasVolumeSelection: true,
    volumeSelectionType: 'primary_and_secondary_cp'
  },
  8: { 
    title: 'Creación de variables dicotómicas', 
    description: 'Create dichotomous variables',
    hasDichotomousVariables: true
  },
  9: { 
    title: 'Eliminar columna', 
    description: 'Remove columns from dataset',
    hasColumnDropping: true
  },
  10: { 
    title: 'Exportar los resultados', 
    description: 'Export processed results',
    hasExport: true
  },
  11: { title: 'Deduplicar con base del cliente', description: 'Deduplicate based on client database', hasDeduplication: true },
  13: {
    title: "Dividir DataFrame en archivos más pequeños",
    description: "Divide el DataFrame en archivos más pequeños basados en un número máximo de filas por archivo.",
    hasSplitDataframe: true
  },
  14: {
    title: 'Count by Groups',
    description: 'Count records by gender or age ranges',
    hasCountByGroups: true
  },
  15: {
    title: "Concatenar archivos",
    description: "Combina múltiples archivos en un solo DataFrame",
    hasConcatenation: true
  },
  16: { title: 'Script de Nominatim para direcciones', description: 'Nominatim script for addresses', hasNominatim: true },
  17: { title: 'Clasificador/ponderador por los 7 clusters', description: 'Classifier/weighting by 7 clusters based on focus group qualitatives and CAI/CAP decision trees', hasClusterClassifier: true },
};

const ScriptPage = () => {
  const { scriptId } = useParams();
  const navigate = useNavigate();
  const script = scripts[scriptId];
  const bgColor = useColorModeValue('white', 'gray.800');
  const toast = useToast();
  const [currentScriptName, setCurrentScriptName] = useState('');
  
  useEffect(() => {
    // Get the current script name from localStorage
    const storedName = localStorage.getItem('currentScriptName');
    if (storedName) {
      setCurrentScriptName(storedName);
    }
  }, []);

  const handleViewChanges = () => {
    navigate('/changes');
  };

  const handleViewData = () => {
    navigate('/data');
  };
  
  // State for file upload
  const [fileType, setFileType] = useState('csv');
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  
  // State for age filter
  const [minAge, setMinAge] = useState('');
  const [maxAge, setMaxAge] = useState('');
  const [filteredResult, setFilteredResult] = useState(null);
  const [isFiltering, setIsFiltering] = useState(false);
  
  // State for normalization options
  const [normalizationOptions, setNormalizationOptions] = useState({
    normalize_gender: false
  });
  const [normalizedResult, setNormalizedResult] = useState(null);
  const [isNormalizing, setIsNormalizing] = useState(false);
  
  // State for column management options
  const [columnManagementOptions, setColumnManagementOptions] = useState({
    drop_na: false,
    move_column: false,
    column_name: '',
    position: ''
  });
  const [managedResult, setManagedResult] = useState(null);
  const [isManaging, setIsManaging] = useState(false);
  
  // State for name processing options
  const [nameProcessingOptions, setNameProcessingOptions] = useState({
    capitalize_names: false,
    separate_surnames: false,
    all_caps: false
  });
  const [processedNamesResult, setProcessedNamesResult] = useState(null);
  const [isProcessingNames, setIsProcessingNames] = useState(false);
  
  // Default lists of allowed names and surnames
  const defaultAllowedNames = [
    'Dan', 'Fer', 'Gio', 'Ian', 'Ibai', 'Íker', 'Ivo', 'Jan', 'Jon', 'Kai', 'Leo', 'Max', 'Nel', 'Neo', 'Noé', 'Pau', 'Pol',
    'Río', 'Roc', 'Roi', 'Roy', 'Sur', 'Teo', 'Ada', 'Amy', 'Ana', 'Ema', 'Eva', 'Fay', 'Fey', 'Lía', 'Luz', 'Mía', 'Nao',
    'Naz', 'Noa', 'Nur', 'Oda', 'Ona', 'Ova', 'Paz', 'Pía', 'Rut', 'Sia', 'Sol', 'Uma', 'Xie', 'Zen', 'Zia', 'Zoe', 'Ale', 'Isa',
    'Mar', 'Liz', 'Bea', 'Edu', 'Noe', 'Ari', 'eli', 'Pep', 'Lia', 'Ane', 'Gil', 'Ter', 'Ion'
  ];
  
  const defaultAllowedSurnames = [
    'Yun', 'Paz', 'Sol', 'Rey', 'Mar', 'Foy', 'Rex', 'Ana', 'Cid', 'Eva', 'Gil', 'Mas', 'Paz',
    'Pla', 'Pop', 'Rey', 'Ruz', 'Pop', 'Rio', 'Luz', 'Amo', 'Cos', 'Vaz', 'Mar', 'Ter'
  ];
  
  // State for name exclusion
  const [exceptions, setExceptions] = useState(defaultAllowedNames.join(', '));
  const [excludedResult, setExcludedResult] = useState(null);
  const [isExcluding, setIsExcluding] = useState(false);
  
  // State for record limiting
  const [numRecords, setNumRecords] = useState(11000);
  const [selectedColumn, setSelectedColumn] = useState('');
  const [limitedResult, setLimitedResult] = useState(null);
  const [isLimiting, setIsLimiting] = useState(false);
  
  // State for volume selection
  const [targetVolumes, setTargetVolumes] = useState({});
  const [cpColumn, setCpColumn] = useState('cp');
  const [ageColumn, setAgeColumn] = useState('edad');
  const [ageRangeFrom, setAgeRangeFrom] = useState('');
  const [ageRangeTo, setAgeRangeTo] = useState('');
  const [primaryCpColumn, setPrimaryCpColumn] = useState('cp');
  const [secondaryCps, setSecondaryCps] = useState([]);
  const [newSecondaryCp, setNewSecondaryCp] = useState('');
  const [selectedVolumeResult, setSelectedVolumeResult] = useState(null);
  const [isSelectingVolume, setIsSelectingVolume] = useState(false);
  const [newCp, setNewCp] = useState('');
  const [newVolume, setNewVolume] = useState('');
  const [targetVolumesFile, setTargetVolumesFile] = useState(null);
  
  // State for dichotomous variables
  const [dichotomousColumn, setDichotomousColumn] = useState('');
  const [dichotomousPrefix, setDichotomousPrefix] = useState('');
  const [dichotomousValues, setDichotomousValues] = useState([]);
  const [dichotomousResult, setDichotomousResult] = useState(null);
  const [isCreatingDichotomous, setIsCreatingDichotomous] = useState(false);
  const [newValue, setNewValue] = useState('');
  const [newOperator, setNewOperator] = useState('eq');
  
  // State for column dropping
  const [columnToDrop, setColumnToDrop] = useState('');
  const [droppedResult, setDroppedResult] = useState(null);
  const [isDroppingColumn, setIsDroppingColumn] = useState(false);
  
  // State for export
  const [exportFormat, setExportFormat] = useState('xlsx');
  const [exportFilename, setExportFilename] = useState('exported_data');
  const [exportResult, setExportResult] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [sendEmail, setSendEmail] = useState(false);
  const [exportEmail, setExportEmail] = useState('');
  
  // State for deduplication
  const [clientFiles, setClientFiles] = useState([]);
  const [deduplicationResult, setDeduplicationResult] = useState(null);
  const [isDeduplicating, setIsDeduplicating] = useState(false);
  
  // State for criteria selection
  const [mainFile, setMainFile] = useState(null);
  const [mainFileType, setMainFileType] = useState('xlsx');
  const [criteriaFile, setCriteriaFile] = useState(null);
  const [criteriaFileType, setCriteriaFileType] = useState('xlsx');
  const [criteriaColumn, setCriteriaColumn] = useState('');
  const [targetColumn, setTargetColumn] = useState('');
  const [criteriaResult, setCriteriaResult] = useState(null);
  const [isSelectingByCriteria, setIsSelectingByCriteria] = useState(false);
  
  // State for splitting dataframe
  const [maxRowsPerFile, setMaxRowsPerFile] = useState(1000);
  const [outputFormat, setOutputFormat] = useState('xlsx');
  const [splitResult, setSplitResult] = useState(null);
  const [isSplitting, setIsSplitting] = useState(false);
  
  // State for concatenation
  const [filesToConcatenate, setFilesToConcatenate] = useState([]);
  const [concatenationResult, setConcatenationResult] = useState(null);
  const [isConcatenating, setIsConcatenating] = useState(false);
  
  // State for Nominatim processing
  const [addressColumn, setAddressColumn] = useState('');
  const [outputColumn, setOutputColumn] = useState('nominatim_result');
  const [delay, setDelay] = useState(1.0);
  const [maxRetries, setMaxRetries] = useState(3);
  const [nominatimResult, setNominatimResult] = useState(null);
  const [isProcessingNominatim, setIsProcessingNominatim] = useState(false);
  const [nominatimLogs, setNominatimLogs] = useState([]);
  const [logContainerRef, setLogContainerRef] = useState(null);
  
  // State for count by groups
  const [groupBy, setGroupBy] = useState('genero');
  const [ageRanges, setAgeRanges] = useState([
    { min: 0, max: 18, label: '0-18' },
    { min: 19, max: 30, label: '19-30' },
    { min: 31, max: 45, label: '31-45' },
    { min: 46, max: Infinity, label: '46+' }
  ]);
  const [countResult, setCountResult] = useState(null);
  const [isCounting, setIsCounting] = useState(false);
  
  // Add new state variables for cluster classifier
  const [clusterOptions, setClusterOptions] = useState({
    audifonos: false,
    energia: false,
    finanzas: false,
    juegos: false,
    ongs: false,
    telcos: false
  });
  const [useCustomFile, setUseCustomFile] = useState(false);
  const [clusterFile, setClusterFile] = useState(null);
  const [clusterFileType, setClusterFileType] = useState('xlsx');
  const [isProcessingCluster, setIsProcessingCluster] = useState(false);
  const [clusterResult, setClusterResult] = useState(null);
  
  // State for Nominatim email
  const [nominatimEmail, setNominatimEmail] = useState('');
  const [direccionColumn, setDireccionColumn] = useState('');
  const [municipioOut, setMunicipioOut] = useState('municipio');
  const [calleOut, setCalleOut] = useState('calle');
  const [tipoViaOut, setTipoViaOut] = useState('tipo_via');
  const [fileExtension, setFileExtension] = useState('csv');
  
  // Store the imported DataFrame in localStorage
  useEffect(() => {
    if (result && scriptId === '1') {
      localStorage.setItem('importedDataFrame', JSON.stringify(result));
    }
  }, [result, scriptId]);
  
  // Load the imported DataFrame from localStorage for script 2
  useEffect(() => {
    if (scriptId === '2') {
      const storedData = localStorage.getItem('importedDataFrame');
      if (storedData) {
        setResult(JSON.parse(storedData));
      } else {
        toast({
          title: 'No data available',
          description: 'Please import a file in script 01 first',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  }, [scriptId, toast]);

  // Load the imported DataFrame from localStorage for script 3
  useEffect(() => {
    if (scriptId === '3') {
      const storedData = localStorage.getItem('importedDataFrame');
      if (storedData) {
        setResult(JSON.parse(storedData));
      } else {
        toast({
          title: 'No data available',
          description: 'Please import a file in script 01 first',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  }, [scriptId, toast]);

  // Load the imported DataFrame from localStorage for script 4
  useEffect(() => {
    if (scriptId === '4') {
      const storedData = localStorage.getItem('importedDataFrame');
      if (storedData) {
        setResult(JSON.parse(storedData));
      } else {
        toast({
          title: 'No data available',
          description: 'Please import a file in script 01 first',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  }, [scriptId, toast]);

  // Load the imported DataFrame from localStorage for script 5
  useEffect(() => {
    if (scriptId === '5') {
      const storedData = localStorage.getItem('importedDataFrame');
      if (storedData) {
        setResult(JSON.parse(storedData));
      } else {
        toast({
          title: 'No data available',
          description: 'Please import a file in script 01 first',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  }, [scriptId, toast]);

  // Load the imported DataFrame from localStorage for script 6
  useEffect(() => {
    if (scriptId === '6') {
      const storedData = localStorage.getItem('importedDataFrame');
      if (storedData) {
        setResult(JSON.parse(storedData));
      } else {
        toast({
          title: 'No data available',
          description: 'Please import a file in script 01 first',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  }, [scriptId, toast]);

  // Load the imported DataFrame from localStorage for script 7
  useEffect(() => {
    if (scriptId === '7') {
      const storedData = localStorage.getItem('importedDataFrame');
      if (storedData) {
        setResult(JSON.parse(storedData));
      } else {
        toast({
          title: 'No data available',
          description: 'Please import a file in script 01 first',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  }, [scriptId, toast]);

  // Load the imported DataFrame from localStorage for scripts 7.2, 7.3, and 7.4
  useEffect(() => {
    if (scriptId === '7.2' || scriptId === '7.3' || scriptId === '7.4') {
      const storedData = localStorage.getItem('importedDataFrame');
      if (storedData) {
        setResult(JSON.parse(storedData));
      } else {
        toast({
          title: 'No data available',
          description: 'Please import a file in script 01 first',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  }, [scriptId, toast]);

  // Load the imported DataFrame from localStorage for scripts 8, 9, 10, and 11
  useEffect(() => {
    if (scriptId === '8' || scriptId === '9' || scriptId === '10' || scriptId === '11') {
      const storedData = localStorage.getItem('importedDataFrame');
      if (storedData) {
        setResult(JSON.parse(storedData));
      } else {
        toast({
          title: 'No data available',
          description: 'Please import a file in script 01 first',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  }, [scriptId, toast]);

  // Load the imported DataFrame from localStorage for count by groups script
  useEffect(() => {
    if (scriptId === '14') {
      const storedData = localStorage.getItem('importedDataFrame');
      if (storedData) {
        setResult(JSON.parse(storedData));
      } else {
        toast({
          title: 'No data available',
          description: 'Please import a file in script 01 first',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  }, [scriptId, toast]);

  useEffect(() => {
    if (scriptId === '17') {
      const storedData = localStorage.getItem('importedDataFrame');
      if (storedData) {
        setResult(JSON.parse(storedData));
      } else {
        toast({
          title: 'No data available',
          description: 'Please import a file in script 01 first',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  }, [scriptId, toast]);

  // Load the imported DataFrame from localStorage for split DataFrame script
  useEffect(() => {
    if (scriptId === '13') {
      const storedData = localStorage.getItem('importedDataFrame');
      if (storedData) {
        setResult(JSON.parse(storedData));
      } else {
        toast({
          title: 'No data available',
          description: 'Please import a file in script 01 first',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  }, [scriptId, toast]);

  if (!script) {
    return (
      <Container maxW="container.xl" py={8}>
        <Heading>Script not found</Heading>
        <Button mt={4} onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  // Helper function to store data without complete_data
  const storeDataWithoutCompleteData = (data) => {
    const { complete_data, ...dataToStore } = data;
    localStorage.setItem('importedDataFrame', JSON.stringify(dataToStore));
    return dataToStore;
  };

  const handleSubmit = async () => {
    if (!file) {
      toast({
        title: 'Error',
        description: 'Please select a file',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    
    try {
      // Convert file to base64
      const base64Content = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result.split(',')[1]);
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(file);
      });
      
      // Send to backend
      const response = await axios.post(`${API_URL}/api/scripts/import`, {
        file_content: base64Content,
        file_type: fileType
      });
      
      // Remove complete_data from response before storing in state
      const dataToStore = storeDataWithoutCompleteData(response.data);
      setResult(dataToStore);
      
      // Store the current script name and dataset_id in localStorage
      const scriptName = file.name;
      localStorage.setItem('currentScriptName', scriptName);
      localStorage.setItem('dataset_id', response.data.dataset_id);
      setCurrentScriptName(scriptName);
      
      toast({
        title: 'Success',
        description: `File loaded successfully with ${response.data.rows} rows and ${response.data.columns.length} columns`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterAge = async () => {
    if (!result) {
      toast({
        title: 'Error',
        description: 'No data available to filter',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setIsFiltering(true);
    
    try {
      // Prepare request payload with only specified age values
      const payload = {
        dataset_id: localStorage.getItem('dataset_id'),
        data_cache_key: result.cache_key
      };
      
      // Only include min_age if it's not empty
      if (minAge !== '') {
        payload.min_age = parseInt(minAge);
      }
      
      // Only include max_age if it's not empty
      if (maxAge !== '') {
        payload.max_age = parseInt(maxAge);
      }
      
      // Send to backend
      const response = await axios.post(`${API_URL}/api/scripts/filter-age`, payload);
      
      // Remove complete_data before setting state
      const dataToStore = storeDataWithoutCompleteData(response.data);
      setFilteredResult(dataToStore);
      
      // Create a description of the filter applied
      let filterDescription = 'Data filtered successfully.';
      if (minAge !== '' && maxAge !== '') {
        filterDescription += ` Age between ${minAge} and ${maxAge}.`;
      } else if (minAge !== '') {
        filterDescription += ` Age greater than or equal to ${minAge}.`;
      } else if (maxAge !== '') {
        filterDescription += ` Age less than or equal to ${maxAge}.`;
      } else {
        filterDescription += ' No age filter applied.';
      }
      filterDescription += ` ${response.data.rows} rows match the criteria.`;
      
      toast({
        title: 'Success',
        description: filterDescription,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsFiltering(false);
    }
  };

  const handleNormalizeVariables = async () => {
    if (!result) {
      toast({
        title: 'Error',
        description: 'No data available to normalize',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    // Check if at least one option is selected
    const hasSelectedOption = Object.values(normalizationOptions).some(value => value === true);
    if (!hasSelectedOption) {
      toast({
        title: 'Error',
        description: 'Please select at least one normalization option',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setIsNormalizing(true);
    
    try {
      // Use the complete data
      
      // Send to backend
      const response = await axios.post(`${API_URL}/api/scripts/normalize-variables`, {
        options: normalizationOptions,
        dataset_id: localStorage.getItem('dataset_id'),
        data_cache_key: result.cache_key
      });
      
      setNormalizedResult(response.data);
      
      // Store the normalized result as the new dataframe in localStorage
      storeDataWithoutCompleteData(response.data);
      
      // Create a description of the normalizations applied
      let normalizationDescription = 'Data normalized successfully. Applied:';
      if (normalizationOptions.normalize_gender) {
        normalizationDescription += ' Gender normalization';
      }
      normalizationDescription += `. ${response.data.rows} rows in the result.`;
      
      toast({
        title: 'Success',
        description: normalizationDescription,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsNormalizing(false);
    }
  };

  const handleManageColumns = async () => {
    if (!result) {
      toast({
        title: 'Error',
        description: 'No data available to process',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    // Check if at least one option is selected
    const hasSelectedOption = columnManagementOptions.drop_na || 
      (columnManagementOptions.move_column && columnManagementOptions.column_name && columnManagementOptions.position);
    
    if (!hasSelectedOption) {
      toast({
        title: 'Error',
        description: 'Please select at least one option and provide required information',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setIsManaging(true);
    
    try {
      // Use the complete data
      
      // Prepare options for the request
      const options = {
        drop_na: columnManagementOptions.drop_na,
        move_column: columnManagementOptions.move_column
      };
      
      // Add column name and position if move_column is selected
      if (columnManagementOptions.move_column) {
        options.column_name = columnManagementOptions.column_name;
        options.position = parseInt(columnManagementOptions.position);
      }
      
      // Send to backend
      const response = await axios.post(`${API_URL}/api/scripts/manage-columns`, {
        options: options,
        dataset_id: localStorage.getItem('dataset_id'),
        data_cache_key: result.cache_key
      });
      
      setManagedResult(response.data);
      
      // Store the managed result as the new dataframe in localStorage
      storeDataWithoutCompleteData(response.data);
      
      // Create a description of the operations applied
      let operationDescription = 'Data processed successfully. Applied:';
      if (columnManagementOptions.drop_na) {
        operationDescription += ' Drop NA values,';
      }
      if (columnManagementOptions.move_column) {
        operationDescription += ` Move column '${columnManagementOptions.column_name}' to position ${columnManagementOptions.position},`;
      }
      operationDescription = operationDescription.slice(0, -1); // Remove trailing comma
      operationDescription += `. ${response.data.rows} rows in the result.`;
      
      toast({
        title: 'Success',
        description: operationDescription,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsManaging(false);
    }
  };

  const handleProcessNames = async () => {
    if (!result) {
      toast({
        title: 'Error',
        description: 'No data available to process',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    // Check if at least one option is selected
    const hasSelectedOption = nameProcessingOptions.capitalize_names || nameProcessingOptions.separate_surnames || nameProcessingOptions.all_caps;
    
    if (!hasSelectedOption) {
      toast({
        title: 'Error',
        description: 'Please select at least one option',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setIsProcessingNames(true);
    
    try {
      // Use the complete data
      
      // Send to backend
      const response = await axios.post(`${API_URL}/api/scripts/process-names`, {
        options: nameProcessingOptions,
        dataset_id: localStorage.getItem('dataset_id'),
        data_cache_key: result.cache_key
      });
      
      setProcessedNamesResult(response.data);
      
      // Store the processed result as the new dataframe in localStorage
      storeDataWithoutCompleteData(response.data);
      
      // Create a description of the operations applied
      let operationDescription = 'Names processed successfully. Applied:';
      if (nameProcessingOptions.capitalize_names) {
        operationDescription += ' Capitalize names,';
      }
      if (nameProcessingOptions.separate_surnames) {
        operationDescription += ' Separate surnames,';
      }
      if (nameProcessingOptions.all_caps) {
        operationDescription += ' Convert to ALL CAPS,';
      }
      operationDescription = operationDescription.slice(0, -1); // Remove trailing comma
      operationDescription += `. ${response.data.rows} rows in the result.`;
      
      toast({
        title: 'Success',
        description: operationDescription,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsProcessingNames(false);
    }
  };

  const handleExcludeNames = async () => {
    if (!result) {
      toast({
        title: 'Error',
        description: 'No data available to process',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    // Parse exceptions into a list
    const exceptionsList = exceptions
      .split(',')
      .map(ex => ex.trim())
      .filter(ex => ex !== '');
    
    setIsExcluding(true);
    
    try {
      // Use the complete data
      
      // Send to backend
      const response = await axios.post(`${API_URL}/api/scripts/exclude-names`, {
        exceptions: exceptionsList,
        dataset_id: localStorage.getItem('dataset_id'),
        data_cache_key: result.cache_key
      });
      
      setExcludedResult(response.data);
      
      // Store the excluded result as the new dataframe in localStorage
      storeDataWithoutCompleteData(response.data);
      
      // Create a description of the operation
      let operationDescription = `Names processed successfully. Excluded ${response.data.excluded_count} names with 3 letters`;
      if (exceptionsList.length > 0) {
        operationDescription += ` (except: ${exceptionsList.join(', ')})`;
      }
      operationDescription += `. ${response.data.rows} rows remaining.`;
      
      toast({
        title: 'Success',
        description: operationDescription,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsExcluding(false);
    }
  };

  const handleLimitRecords = async () => {
    if (!result) {
      toast({
        title: 'Error',
        description: 'No data available to process',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    if (!selectedColumn) {
      toast({
        title: 'Error',
        description: 'Please select a column',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    if (numRecords <= 0) {
      toast({
        title: 'Error',
        description: 'Number of records must be greater than 0',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setIsLimiting(true);
    
    try {
      // Use the complete data
      
      // Send to backend
      const response = await axios.post(`${API_URL}/api/scripts/limit-records`, {
        num_records: numRecords,
        column_name: selectedColumn,
        dataset_id: localStorage.getItem('dataset_id'),
        data_cache_key: result.cache_key
      });
      
      setLimitedResult(response.data);
      
      // Store the limited result as the new dataframe in localStorage
      storeDataWithoutCompleteData(response.data);
      
      // Create a description of the operation
      let operationDescription = `Records limited successfully. Total records: ${response.data.rows}`;
      operationDescription += `. Distribution by ${selectedColumn}:`;
      
      // Add distribution information
      const distribution = response.data.value_distribution;
      for (const [value, count] of Object.entries(distribution)) {
        operationDescription += `\n- ${value}: ${count}`;
      }
      
      toast({
        title: 'Success',
        description: operationDescription,
        status: 'success',
        duration: 8000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLimiting(false);
    }
  };

  const handleAddTargetVolume = () => {
    if (!newCp || !newVolume) {
      toast({
        title: 'Error',
        description: 'Please enter both postal code and target volume',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    const volume = parseInt(newVolume);
    if (isNaN(volume) || volume <= 0) {
      toast({
        title: 'Error',
        description: 'Target volume must be a positive number',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setTargetVolumes({
      ...targetVolumes,
      [newCp]: volume
    });
    
    setNewCp('');
    setNewVolume('');
  };
  
  const handleRemoveTargetVolume = (cp) => {
    const newTargetVolumes = { ...targetVolumes };
    delete newTargetVolumes[cp];
    setTargetVolumes(newTargetVolumes);
  };
  
  const handleAddSecondaryCp = () => {
    if (!newSecondaryCp) {
      toast({
        title: 'Error',
        description: 'Please enter a postal code',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (secondaryCps.includes(newSecondaryCp)) {
      toast({
        title: 'Error',
        description: 'This postal code is already in the list',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSecondaryCps([...secondaryCps, newSecondaryCp]);
    setNewSecondaryCp('');
  };

  const handleRemoveSecondaryCp = (cp) => {
    setSecondaryCps(secondaryCps.filter(c => c !== cp));
  };
  
  const handleSelectVolume = async () => {
    if (!result) {
      toast({
        title: 'Error',
        description: 'No data available to process',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!targetVolumesFile) {
      toast({
        title: 'Error',
        description: 'Please select a target volumes file',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (scriptId === '7.3' && !ageColumn) {
      toast({
        title: 'Error',
        description: 'Please select an age column',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setIsSelectingVolume(true);
    
    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Content = e.target.result.split(',')[1];
        
        let response;
        
        // Send to backend based on the script type
        if (scriptId === '7.2') {
          response = await axios.post(`${API_URL}/api/scripts/select-volume-by-cp`, {
            excel_base64: base64Content,
            cp_column: cpColumn,
            dataset_id: localStorage.getItem('dataset_id'),
            data_cache_key: result.cache_key
          });
        } else if (scriptId === '7.3') {
          response = await axios.post(`${API_URL}/api/scripts/select-volume-by-cp-and-age`, {
            excel_base64: base64Content,
            cp_column: cpColumn,
            age_column: ageColumn,
            dataset_id: localStorage.getItem('dataset_id'),
            data_cache_key: result.cache_key
          });
        } else if (scriptId === '7.4') {
          response = await axios.post(`${API_URL}/api/scripts/select-volume-by-primary-and-secondary-cp`, {
            excel_base64: base64Content,
            primary_cp_column: cpColumn,
            dataset_id: localStorage.getItem('dataset_id'),
            data_cache_key: result.cache_key
          });
        }
        
        setSelectedVolumeResult(response.data);
        
        // Store the selected volume result as the new dataframe in localStorage
        storeDataWithoutCompleteData(response.data);
        
        // Create a description of the operation
        let operationDescription = `Volume selection completed successfully. Total records: ${response.data.rows}`;
        operationDescription += `. Distribution by postal code:`;
        
        // Add distribution information
        const distribution = response.data.value_distribution;
        for (const [cp, count] of Object.entries(distribution)) {
          operationDescription += `\n- ${cp}: ${count}`;
        }
        
        toast({
          title: 'Success',
          description: operationDescription,
          status: 'success',
          duration: 8000,
          isClosable: true,
        });
      };
      
      reader.readAsDataURL(targetVolumesFile);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSelectingVolume(false);
    }
  };

  const handleAddDichotomousValue = () => {
    if (!newValue) {
      toast({
        title: 'Error',
        description: 'Please enter a value',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const numValue = parseFloat(newValue);
    if (isNaN(numValue)) {
      toast({
        title: 'Error',
        description: 'Please enter a valid number',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setDichotomousValues([...dichotomousValues, { value: numValue, operator: newOperator }]);
    setNewValue('');
  };

  const handleRemoveDichotomousValue = (index) => {
    const newValues = [...dichotomousValues];
    newValues.splice(index, 1);
    setDichotomousValues(newValues);
  };

  const handleCreateDichotomous = async () => {
    if (!result) {
      toast({
        title: 'Error',
        description: 'No data available to process',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    if (!dichotomousColumn) {
      toast({
        title: 'Error',
        description: 'Please select a column',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    if (dichotomousValues.length === 0) {
      toast({
        title: 'Error',
        description: 'Please add at least one value and operator pair',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setIsCreatingDichotomous(true);
    
    try {
      // Send to backend
      const response = await axios.post(`${API_URL}/api/scripts/create-dichotomous`, {
        options: {
          column_name: dichotomousColumn,
          prefix: dichotomousPrefix,
          values: dichotomousValues
        },
        dataset_id: localStorage.getItem('dataset_id'),
        data_cache_key: result.cache_key
      });
      
      setDichotomousResult(response.data);
      
      // Store the dichotomous result as the new dataframe in localStorage
      storeDataWithoutCompleteData(response.data);
      
      // Create a description of the operation
      let operationDescription = `Dichotomous variables created successfully. Total rows: ${response.data.rows}`;
      operationDescription += `. New columns: ${dichotomousValues.map(v => `${dichotomousPrefix}${v.operator}_${v.value}`).join(', ')}`;
      
      toast({
        title: 'Success',
        description: operationDescription,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsCreatingDichotomous(false);
    }
  };
  
  const handleDropColumn = async () => {
    if (!result) {
      toast({
        title: 'Error',
        description: 'No data available to process',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    if (!columnToDrop) {
      toast({
        title: 'Error',
        description: 'Please select a column to drop',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setIsDroppingColumn(true);
    
    try {
      // Use the complete data
      
      // Send to backend
      const response = await axios.post(`${API_URL}/api/scripts/drop-column`, {
        column_name: columnToDrop,
        dataset_id: localStorage.getItem('dataset_id'),
        data_cache_key: result.cache_key
      });
      
      setDroppedResult(response.data);
      
      // Store the dropped result as the new dataframe in localStorage
      storeDataWithoutCompleteData(response.data);
      
      // Create a description of the operation
      let operationDescription = `Column '${columnToDrop}' dropped successfully. Total rows: ${response.data.rows}`;
      
      toast({
        title: 'Success',
        description: operationDescription,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDroppingColumn(false);
    }
  };
  
  const handleExport = async () => {
    if (!result) {
      toast({
        title: 'Error',
        description: 'No data available to export',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    if (!exportFilename) {
      toast({
        title: 'Error',
        description: 'Please enter a filename',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (sendEmail && !exportEmail) {
      toast({
        title: 'Error',
        description: 'Please enter an email address',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setIsExporting(true);
    
    try {
      // Send to backend
      const response = await axios.post(`${API_URL}/api/scripts/export-results`, {
        file_format: exportFormat,
        filename: exportFilename,
        dataset_id: localStorage.getItem('dataset_id'),
        data_cache_key: result.cache_key,
        email: sendEmail ? exportEmail : undefined
      });
      
      setExportResult(response.data);
      
      if (sendEmail) {
        // For email exports, just show success message
        toast({
          title: 'Success',
          description: `File will be sent to ${exportEmail}`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        // For direct downloads, create download link
        const fileContent = response.data.file_content;
        const contentType = response.data.content_type;
        const filename = response.data.filename;
        
        // Create a blob and download link
        const byteCharacters = atob(fileContent);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: contentType });
        
        // Create a download link and trigger download
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        toast({
          title: 'Success',
          description: `File exported successfully as ${filename}`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleAddClientFile = () => {
    if (!file) {
      toast({
        title: 'Error',
        description: 'Please select a file',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    // Convert file to base64
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64Content = e.target.result.split(',')[1];
      
      // Add file to the list
      setClientFiles([
        ...clientFiles,
        {
          file_content: base64Content,
          file_type: fileType,
          filename: file.name
        }
      ]);
      
      // Reset file input
      setFile(null);
      
      toast({
        title: 'File added',
        description: `${file.name} added to client database files`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    };
    
    reader.readAsDataURL(file);
  };
  
  const handleRemoveClientFile = (index) => {
    const newClientFiles = [...clientFiles];
    newClientFiles.splice(index, 1);
    setClientFiles(newClientFiles);
  };
  
  const handleDeduplicate = async () => {
    if (!result) {
      toast({
        title: 'Error',
        description: 'No data available to deduplicate',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    if (clientFiles.length === 0) {
      toast({
        title: 'Error',
        description: 'Please add at least one client database file',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setIsDeduplicating(true);
    
    try {
      // Use the complete data
      
      // Send to backend
      const response = await axios.post(`${API_URL}/api/scripts/deduplicate`, {
        client_files: clientFiles,
        dataset_id: localStorage.getItem('dataset_id'),
        data_cache_key: result.cache_key
      });
      
      setDeduplicationResult(response.data);
      
      // Store the deduplicated result as the new dataframe in localStorage
      storeDataWithoutCompleteData(response.data);
      
      // Create a description of the operation
      let operationDescription = `Deduplication completed successfully. Total rows: ${response.data.rows}`;
      operationDescription += `. Removed ${response.data.removed_records} records.`;
      
      toast({
        title: 'Success',
        description: operationDescription,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDeduplicating(false);
    }
  };

  const handleMainFileChange = (e) => {
    setMainFile(e.target.files[0]);
  };
  
  const handleCriteriaFileChange = (e) => {
    setCriteriaFile(e.target.files[0]);
  };
  
  const handleSelectByCriteria = async () => {
    if (!mainFile) {
      toast({
        title: 'Error',
        description: 'Please select a main file',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    if (!criteriaFile) {
      toast({
        title: 'Error',
        description: 'Please select a criteria file',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    if (!criteriaColumn) {
      toast({
        title: 'Error',
        description: 'Please select a criteria column',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    
    setIsSelectingByCriteria(true);
    
    try {
      // Convert files to base64
      const mainFileBase64 = await fileToBase64(mainFile);
      const criteriaFileBase64 = await fileToBase64(criteriaFile);
      
      // Send to backend
      const response = await axios.post(`${API_URL}/api/scripts/select-by-criteria`, {
        main_file_content: mainFileBase64,
        main_file_type: mainFileType,
        criteria_file_content: criteriaFileBase64,
        criteria_file_type: criteriaFileType,
        criteria_column: criteriaColumn,
        target_column: targetColumn || undefined,
        dataset_id: localStorage.getItem('dataset_id'),
        data_cache_key: result.cache_key
      });
      
      setCriteriaResult(response.data);
      
      // Store the result as the new dataframe in localStorage
      storeDataWithoutCompleteData(response.data);
      
      // Create a description of the operation
      let operationDescription = `Selection completed successfully. Total rows: ${response.data.rows}`;
      operationDescription += `. Matched ${response.data.matched_records} records.`;
      operationDescription += `. Removed ${response.data.removed_records} records.`;
      
      toast({
        title: 'Success',
        description: operationDescription,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSelectingByCriteria(false);
    }
  };
  
  // Helper function to convert file to base64
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64Content = e.target.result.split(',')[1];
        resolve(base64Content);
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  const handleSplitDataframe = async () => {
    if (!result) {
      toast({
        title: 'Error',
        description: 'Please upload a file first',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSplitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/scripts/split-dataframe`, {
        max_rows_per_file: maxRowsPerFile,
        output_format: outputFormat,
        dataset_id: localStorage.getItem('dataset_id'),
        data_cache_key: result.cache_key
      });

      if (response.data.success) {
        setSplitResult(response.data);
        toast({
          title: 'Success',
          description: 'DataFrame split successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        throw new Error(response.data.error || 'Failed to split DataFrame');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'An error occurred while splitting the DataFrame',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSplitting(false);
    }
  };

  const handleAddFileToConcatenate = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const fileContent = await fileToBase64(file);
        const fileType = file.name.toLowerCase().endsWith('.xlsx') ? 'xlsx' : 'csv';
        
        setFilesToConcatenate(prev => [...prev, {
          name: file.name,
          file_content: fileContent,
          file_type: fileType
        }]);
        
        toast({
          title: 'Success',
          description: `File ${file.name} added successfully`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } catch (error) {
        console.error('Error:', error);
        toast({
          title: 'Error',
          description: 'Error processing file',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    }
  };

  const handleRemoveFileFromConcatenate = (index) => {
    setFilesToConcatenate(prev => prev.filter((_, i) => i !== index));
  };

  const handleConcatenateFiles = async () => {
    if (filesToConcatenate.length < 2) {
      toast({
        title: 'Error',
        description: 'Please add at least two files to concatenate',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsConcatenating(true);
    try {
      const response = await fetch(`${API_URL}/api/scripts/concatenate-files`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          files: filesToConcatenate,
          dataset_id: localStorage.getItem('dataset_id')
        }),
      });

      const result = await response.json();
      if (result.success) {
        setConcatenationResult(result);
        storeDataWithoutCompleteData(result);
        toast({
          title: 'Success',
          description: `Files concatenated successfully. Total rows: ${result.total_rows}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        throw new Error(result.error || 'Failed to concatenate files');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsConcatenating(false);
    }
  };

  const handleNominatimAddresses = async () => {
    if (!direccionColumn) {
      toast({
        title: 'Error',
        description: 'Please select an address column',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!cpColumn) {
      toast({
        title: 'Error',
        description: 'Please select a postal code column',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!nominatimEmail) {
      toast({
        title: 'Error',
        description: 'Please enter an email address',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsProcessingNominatim(true);
    setNominatimLogs([]);

    try {
      const importedData = localStorage.getItem('importedDataFrame');
      const data = importedData ? JSON.parse(importedData) : null;

      if (!data || !data.cache_key) {
        throw new Error('No data available or missing cache key');
      }

      const response = await fetch(`${API_URL}/api/scripts/nominatim`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          direccion_column: direccionColumn,
          cp_column: cpColumn,
          municipio_out: municipioOut,
          calle_out: calleOut,
          tipo_via_out: tipoViaOut,
          email: nominatimEmail,
          dataset_id: localStorage.getItem('dataset_id'),
          data_cache_key: data.cache_key,
          extension: fileExtension
        }),
      });

      const result = await response.json();
      if (result.success) {
        toast({
          title: 'Success',
          description: result.message || 'Nominatim addresses processing started. Results will be sent to your email when complete.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        throw new Error(result.error || 'Failed to process addresses');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsProcessingNominatim(false);
    }
  };

  const handleCountByGroups = async () => {
    if (!result) {
      toast({
        title: 'Error',
        description: 'No data available to count',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsCounting(true);

    try {
      const response = await axios.post(`${API_URL}/api/scripts/count-by-groups`, {
        group_by: groupBy,
        age_ranges: ageRanges,
        dataset_id: localStorage.getItem('dataset_id'),
        data_cache_key: result.cache_key
      });

      if (response.data.success) {
        setCountResult(response.data);
        toast({
          title: 'Success',
          description: 'Groups counted successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        throw new Error(response.data.error || 'Failed to count groups');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'An error occurred while counting groups',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsCounting(false);
    }
  };

  const operatorSymbols = {
    eq: 'eq',
    gt: 'gt',
    gte: 'gte',
    lt: 'lt',
    lte: 'lte'
  };

  const handleClusterClassification = async () => {
    if (!result && !useCustomFile) {
      toast({
        title: 'Error',
        description: 'No data available to process',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (useCustomFile && !clusterFile) {
      toast({
        title: 'Error',
        description: 'Please select a file',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsProcessingCluster(true);

    try {
      let payload = {
        options: clusterOptions
      };

      if (useCustomFile) {
        // Convert file to base64
        const reader = new FileReader();
        reader.onload = async (e) => {
          const base64Content = e.target.result.split(',')[1];
          payload.file_info = {
            file_content: base64Content,
            file_type: clusterFileType
          };
          payload.dataset_id = localStorage.getItem('dataset_id');

          const response = await axios.post(`${API_URL}/api/scripts/cluster`, payload);
          setClusterResult(response.data);
          localStorage.setItem('dataset_id', response.data.dataset_id);
          toast({
            title: 'Success',
            description: 'Cluster classification completed successfully',
            status: 'success',
            duration: 5000,
            isClosable: true,
          });
        };
        reader.readAsDataURL(clusterFile);
      } else {
        payload.dataset_id = localStorage.getItem('dataset_id');
        payload.data_cache_key = result.cache_key;

        const response = await axios.post(`${API_URL}/api/scripts/cluster`, payload);
        setClusterResult(response.data);
        toast({
          title: 'Success',
          description: 'Cluster classification completed successfully',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'An error occurred',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsProcessingCluster(false);
    }
  };

  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={6} align="stretch">
        <Box p={6} bg={bgColor} borderRadius="lg" boxShadow="md">
          <HStack justify="space-between" align="center" mb={4}>
            <Box>
              <Heading>{script.title}</Heading>
              <Text mt={2}>{script.description}</Text>
              {currentScriptName && (
                <Text mt={2} fontSize="md" color="gray.600">
                  Current File: {currentScriptName}
                </Text>
              )}
            </Box>
            {currentScriptName && (
              <HStack spacing={4}>
                <Button onClick={handleViewChanges} colorScheme="blue" variant="outline">
                  Cambios
                </Button>
                <Button onClick={handleViewData} colorScheme="blue" variant="outline">
                  Data
                </Button>
              </HStack>
            )}
          </HStack>
          
          {script.hasClusterClassifier ? (
            <VStack spacing={4} align="stretch">
              <Box p={4} bg="blue.50" borderRadius="md">
                <Text fontWeight="bold">Cluster Classification Options</Text>
                <VStack align="start" spacing={2} mt={4}>
                  {Object.entries(clusterOptions).map(([key, value]) => (
                    <Checkbox
                      key={key}
                      isChecked={value}
                      onChange={(e) => setClusterOptions({
                        ...clusterOptions,
                        [key]: e.target.checked
                      })}
                    >
                      {key.charAt(0).toUpperCase() + key.slice(1)}
                    </Checkbox>
                  ))}
                </VStack>
              </Box>

              <FormControl>
                <Checkbox
                  isChecked={useCustomFile}
                  onChange={(e) => setUseCustomFile(e.target.checked)}
                >
                  Use custom file
                </Checkbox>
              </FormControl>

              {useCustomFile ? (
                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <FormLabel>File Type</FormLabel>
                    <RadioGroup value={clusterFileType} onChange={setClusterFileType}>
                      <Radio value="xlsx">Excel (.xlsx)</Radio>
                      <Radio value="csv">CSV (.csv)</Radio>
                    </RadioGroup>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Select File</FormLabel>
                    <Input
                      type="file"
                      accept={clusterFileType === 'xlsx' ? '.xlsx' : '.csv'}
                      onChange={(e) => setClusterFile(e.target.files[0])}
                    />
                  </FormControl>
                </VStack>
              ) : (
                <Box p={4} bg="blue.50" borderRadius="md">
                  <Text fontWeight="bold">Current Data Summary:</Text>
                  <Text>Rows: {result?.rows}</Text>
                  <Text>Columns: {Array.isArray(result?.columns) ? result.columns.join(', ') : 'N/A'}</Text>
                </Box>
              )}

              <Button
                colorScheme="blue"
                onClick={handleClusterClassification}
                isLoading={isProcessingCluster}
                loadingText="Processing"
              >
                Process Cluster Classification
              </Button>

              {clusterResult && (
                <Box mt={4}>
                  <Heading size="md" mb={2}>Classification Results</Heading>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
                    <Stat>
                      <StatLabel>Total Rows</StatLabel>
                      <StatNumber>{clusterResult.rows}</StatNumber>
                      <StatHelpText>Processed records</StatHelpText>
                    </Stat>
                    <Stat>
                      <StatLabel>Columns</StatLabel>
                      <StatNumber>{clusterResult.columns.length}</StatNumber>
                      <StatHelpText>Including new columns</StatHelpText>
                    </Stat>
                  </SimpleGrid>

                  <Heading size="sm" mt={4} mb={2}>Sample Data (First 5 rows)</Heading>
                  <Box overflowX="auto">
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          {clusterResult.columns.map((column) => (
                            <Th key={column}>{column}</Th>
                          ))}
                        </Tr>
                      </Thead>
                      <Tbody>
                        {(typeof clusterResult.sample_data === 'string' 
                          ? JSON.parse(clusterResult.sample_data) 
                          : clusterResult.sample_data
                        ).map((row, rowIndex) => (
                          <Tr key={rowIndex}>
                            {clusterResult.columns.map((column) => (
                              <Td key={`${rowIndex}-${column}`}>{row[column]}</Td>
                            ))}
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </Box>
              )}
            </VStack>
          ) : script.hasFileUpload ? (
            <VStack spacing={4} align="stretch">
              <FormControl>
                <FormLabel>File Type</FormLabel>
                <RadioGroup value={fileType} onChange={setFileType}>
                  <Radio value="xlsx">Excel (.xlsx)</Radio>
                  <Radio value="csv">CSV (.csv)</Radio>
                </RadioGroup>
              </FormControl>
              
              <FormControl>
                <FormLabel>Select File</FormLabel>
                <Input 
                  type="file" 
                  accept={fileType === 'xlsx' ? '.xlsx' : '.csv'} 
                  onChange={handleFileChange}
                />
              </FormControl>
              
              <Button 
                colorScheme="blue" 
                onClick={handleSubmit} 
                isLoading={isLoading}
                loadingText="Processing"
              >
                Upload and Process
              </Button>
              
              {result && (
                <Box mt={4}>
                  <Heading size="md" mb={2}>Results</Heading>
                  <Text>Rows: {result.rows}</Text>
          <Text>Columns: {Array.isArray(result?.columns) ? result.columns.join(', ') : 'N/A'}</Text>
                  
                  <Heading size="sm" mt={4} mb={2}>Sample Data (First 5 rows)</Heading>
                  <Box overflowX="auto">
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          {result.columns.map((column) => (
                            <Th key={column}>{column}</Th>
                          ))}
                        </Tr>
                      </Thead>
                      <Tbody>
                        {JSON.parse(result.sample_data).map((row, rowIndex) => (
                          <Tr key={rowIndex}>
                            {result.columns.map((column) => (
                              <Td key={`${rowIndex}-${column}`}>{row[column]}</Td>
                            ))}
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </Box>
              )}
            </VStack>
          ) : script.hasAgeFilter ? (
            <VStack spacing={4} align="stretch">
              {result ? (
                <>
                  <Box p={4} bg="blue.50" borderRadius="md">
                    <Text fontWeight="bold">Imported Data Summary:</Text>
                    <Text>Rows: {result.rows}</Text>
          <Text>Columns: {Array.isArray(result?.columns) ? result.columns.join(', ') : 'N/A'}</Text>
                  </Box>
                  
                  <FormControl>
                    <FormLabel>Age Range (Optional)</FormLabel>
                    <HStack>
                      <NumberInput 
                        value={minAge} 
                        onChange={(value) => setMinAge(value)}
                        min={0}
                        max={maxAge !== '' ? parseInt(maxAge) : 120}
                        placeholder="Min Age"
                      >
                        <NumberInputField placeholder="Min Age" />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                      <Text>to</Text>
                      <NumberInput 
                        value={maxAge} 
                        onChange={(value) => setMaxAge(value)}
                        min={minAge !== '' ? parseInt(minAge) : 0}
                        max={120}
                        placeholder="Max Age"
                      >
                        <NumberInputField placeholder="Max Age" />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </HStack>
                    <HStack mt={2}>
                      <Button size="sm" onClick={() => setMinAge('')}>Clear Min</Button>
                      <Button size="sm" onClick={() => setMaxAge('')}>Clear Max</Button>
                    </HStack>
                  </FormControl>
                  
                  <Button 
                    colorScheme="blue" 
                    onClick={handleFilterAge} 
                    isLoading={isFiltering}
                    loadingText="Filtering"
                  >
                    Filter by Age
                  </Button>
                  
                  {filteredResult && (
                    <Box mt={4}>
                      <Heading size="md" mb={2}>Filtered Results</Heading>
                      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={4}>
                        <Stat>
                          <StatLabel>Total Rows</StatLabel>
                          <StatNumber>{filteredResult.rows}</StatNumber>
                          <StatHelpText>After filtering</StatHelpText>
                        </Stat>
                        <Stat>
                          <StatLabel>Age Range</StatLabel>
                          <StatNumber>
                            {filteredResult.min_age !== null && filteredResult.max_age !== null 
                              ? `${filteredResult.min_age} - ${filteredResult.max_age}`
                              : filteredResult.min_age !== null 
                                ? `≥ ${filteredResult.min_age}`
                                : filteredResult.max_age !== null 
                                  ? `≤ ${filteredResult.max_age}`
                                  : 'No filter'}
                          </StatNumber>
                          <StatHelpText>Years</StatHelpText>
                        </Stat>
                        <Stat>
                          <StatLabel>Removed Rows</StatLabel>
                          <StatNumber>{result.rows - filteredResult.rows}</StatNumber>
                          <StatHelpText>Excluded from dataset</StatHelpText>
                        </Stat>
                      </SimpleGrid>
                      
                      <Heading size="sm" mt={4} mb={2}>Age Distribution</Heading>
                      <Box overflowX="auto" mb={4}>
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              <Th>Age</Th>
                              <Th>Count</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {Object.entries(filteredResult.age_distribution).map(([age, count]) => (
                              <Tr key={age}>
                                <Td>{age}</Td>
                                <Td>{count}</Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                      
                      <Heading size="sm" mt={4} mb={2}>Sample Data (First 5 rows)</Heading>
                      <Box overflowX="auto">
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              {filteredResult.columns.map((column) => (
                                <Th key={column}>{column}</Th>
                              ))}
                            </Tr>
                          </Thead>
                          <Tbody>
                            {JSON.parse(filteredResult.sample_data).map((row, rowIndex) => (
                              <Tr key={rowIndex}>
                                {filteredResult.columns.map((column) => (
                                  <Td key={`${rowIndex}-${column}`}>{row[column]}</Td>
                                ))}
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                    </Box>
                  )}
                </>
              ) : (
                <Box p={4} bg="yellow.50" borderRadius="md">
                  <Text>Please import a file in Script 01 first, then return to this page.</Text>
                  <Button mt={2} colorScheme="blue" onClick={() => navigate('/script/1')}>
                    Go to Script 01
                  </Button>
                </Box>
              )}
            </VStack>
          ) : script.hasNormalization ? (
            <VStack spacing={4} align="stretch">
              {result ? (
                <>
                  <Box p={4} bg="blue.50" borderRadius="md">
                    <Text fontWeight="bold">Imported Data Summary:</Text>
                    <Text>Rows: {result.rows}</Text>
          <Text>Columns: {Array.isArray(result?.columns) ? result.columns.join(', ') : 'N/A'}</Text>
                  </Box>
                  
                  <FormControl>
                    <FormLabel>Normalization Options</FormLabel>
                    <VStack align="start" spacing={2}>
                      <Checkbox 
                        isChecked={normalizationOptions.normalize_gender}
                        onChange={(e) => setNormalizationOptions({
                          ...normalizationOptions,
                          normalize_gender: e.target.checked
                        })}
                      >
                        Normalize gender
                      </Checkbox>
                    </VStack>
                  </FormControl>
                  
                  <Button 
                    colorScheme="blue" 
                    onClick={handleNormalizeVariables} 
                    isLoading={isNormalizing}
                    loadingText="Normalizing"
                  >
                    Normalize Variables
                  </Button>
                  
                  {normalizedResult && (
                    <Box mt={4}>
                      <Heading size="md" mb={2}>Normalized Results</Heading>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
                        <Stat>
                          <StatLabel>Total Rows</StatLabel>
                          <StatNumber>{normalizedResult.rows}</StatNumber>
                          <StatHelpText>After normalization</StatHelpText>
                        </Stat>
                        <Stat>
                          <StatLabel>Columns</StatLabel>
                          <StatNumber>{normalizedResult.columns.length}</StatNumber>
                          <StatHelpText>Including new columns</StatHelpText>
                        </Stat>
                      </SimpleGrid>
                      
                      <Heading size="sm" mt={4} mb={2}>Sample Data (First 5 rows)</Heading>
                      <Box overflowX="auto">
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              {normalizedResult.columns.map((column) => (
                                <Th key={column}>{column}</Th>
                              ))}
                            </Tr>
                          </Thead>
                          <Tbody>
                            {JSON.parse(normalizedResult.sample_data).map((row, rowIndex) => (
                              <Tr key={rowIndex}>
                                {normalizedResult.columns.map((column) => (
                                  <Td key={`${rowIndex}-${column}`}>{row[column]}</Td>
                                ))}
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                    </Box>
                  )}
                </>
              ) : (
                <Box p={4} bg="yellow.50" borderRadius="md">
                  <Text>Please import a file in Script 01 first, then return to this page.</Text>
                  <Button mt={2} colorScheme="blue" onClick={() => navigate('/script/1')}>
                    Go to Script 01
                  </Button>
                </Box>
              )}
            </VStack>
          ) : script.hasColumnManagement ? (
            <VStack spacing={4} align="stretch">
              {result ? (
                <>
                  <Box p={4} bg="blue.50" borderRadius="md">
                    <Text fontWeight="bold">Imported Data Summary:</Text>
                    <Text>Rows: {result.rows}</Text>
          <Text>Columns: {Array.isArray(result?.columns) ? result.columns.join(', ') : 'N/A'}</Text>
                  </Box>
                  
                  <FormControl>
                    <FormLabel>Column Management Options</FormLabel>
                    <VStack align="start" spacing={4}>
                      <Checkbox 
                        isChecked={columnManagementOptions.drop_na}
                        onChange={(e) => setColumnManagementOptions({
                          ...columnManagementOptions,
                          drop_na: e.target.checked
                        })}
                      >
                        Drop rows with NA values
                      </Checkbox>
                      
                      <Box width="100%">
                        <FormControl>
                          <FormLabel>Move Column</FormLabel>
                          <Select
                            placeholder="Select a column to move"
                            value={columnManagementOptions.column_name}
                            onChange={(e) => setColumnManagementOptions({
                              ...columnManagementOptions,
                              move_column: e.target.value !== '',
                              column_name: e.target.value
                            })}
                          >
                            <option value="">None</option>
                            {result.columns.map((column) => (
                              <option key={column} value={column}>{column}</option>
                            ))}
                          </Select>
                        </FormControl>
                        
                        {columnManagementOptions.move_column && (
                          <FormControl mt={2}>
                            <FormLabel>Target Position</FormLabel>
                            <Select
                              placeholder="Select target position"
                              value={columnManagementOptions.position}
                              onChange={(e) => setColumnManagementOptions({
                                ...columnManagementOptions,
                                position: e.target.value
                              })}
                            >
                              {Array.from({ length: result.columns.length }, (_, i) => (
                                <option key={i} value={i}>{i}</option>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      </Box>
                    </VStack>
                  </FormControl>
                  
                  <Button 
                    colorScheme="blue" 
                    onClick={handleManageColumns} 
                    isLoading={isManaging}
                    loadingText="Processing"
                  >
                    Process Columns
                  </Button>
                  
                  {managedResult && (
                    <Box mt={4}>
                      <Heading size="md" mb={2}>Processed Results</Heading>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
                        <Stat>
                          <StatLabel>Total Rows</StatLabel>
                          <StatNumber>{managedResult.rows}</StatNumber>
                          <StatHelpText>After processing</StatHelpText>
                        </Stat>
                        <Stat>
                          <StatLabel>Columns</StatLabel>
                          <StatNumber>{managedResult.columns.length}</StatNumber>
                          <StatHelpText>Column order may have changed</StatHelpText>
                        </Stat>
                      </SimpleGrid>
                      
                      <Heading size="sm" mt={4} mb={2}>Sample Data (First 5 rows)</Heading>
                      <Box overflowX="auto">
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              {managedResult.columns.map((column) => (
                                <Th key={column}>{column}</Th>
                              ))}
                            </Tr>
                          </Thead>
                          <Tbody>
                            {JSON.parse(managedResult.sample_data).map((row, rowIndex) => (
                              <Tr key={rowIndex}>
                                {managedResult.columns.map((column) => (
                                  <Td key={`${rowIndex}-${column}`}>{row[column]}</Td>
                                ))}
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                    </Box>
                  )}
                </>
              ) : (
                <Box p={4} bg="yellow.50" borderRadius="md">
                  <Text>Please import a file in Script 01 first, then return to this page.</Text>
                  <Button mt={2} colorScheme="blue" onClick={() => navigate('/script/1')}>
                    Go to Script 01
                  </Button>
                </Box>
              )}
            </VStack>
          ) : script.hasNameProcessing ? (
            <VStack spacing={4} align="stretch">
              {result ? (
                <>
                  <Box p={4} bg="blue.50" borderRadius="md">
                    <Text fontWeight="bold">Imported Data Summary:</Text>
                    <Text>Rows: {result.rows}</Text>
          <Text>Columns: {Array.isArray(result?.columns) ? result.columns.join(', ') : 'N/A'}</Text>
                  </Box>
                  
                  <FormControl>
                    <FormLabel>Name Processing Options</FormLabel>
                    <VStack align="start" spacing={4}>
                      <Checkbox 
                        isChecked={nameProcessingOptions.capitalize_names}
                        onChange={(e) => setNameProcessingOptions({
                          ...nameProcessingOptions,
                          capitalize_names: e.target.checked
                        })}
                      >
                        Capitalize names and surnames
                      </Checkbox>
                      
                      <Checkbox 
                        isChecked={nameProcessingOptions.all_caps}
                        onChange={(e) => setNameProcessingOptions({
                          ...nameProcessingOptions,
                          all_caps: e.target.checked
                        })}
                      >
                        Todo en mayúsculas
                      </Checkbox>
                      
                      <Checkbox 
                        isChecked={nameProcessingOptions.separate_surnames}
                        onChange={(e) => setNameProcessingOptions({
                          ...nameProcessingOptions,
                          separate_surnames: e.target.checked
                        })}
                      >
                        Separate surnames into two columns (apellido1, apellido2)
                      </Checkbox>
                    </VStack>
                  </FormControl>
                  
                  <Button 
                    colorScheme="blue" 
                    onClick={handleProcessNames} 
                    isLoading={isProcessingNames}
                    loadingText="Processing"
                  >
                    Process Names
                  </Button>
                  
                  {processedNamesResult && (
                    <Box mt={4}>
                      <Heading size="md" mb={2}>Processed Results</Heading>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
                        <Stat>
                          <StatLabel>Total Rows</StatLabel>
                          <StatNumber>{processedNamesResult.rows}</StatNumber>
                          <StatHelpText>After processing</StatHelpText>
                        </Stat>
                        <Stat>
                          <StatLabel>Columns</StatLabel>
                          <StatNumber>{processedNamesResult.columns.length}</StatNumber>
                          <StatHelpText>Including new columns</StatHelpText>
                        </Stat>
                      </SimpleGrid>
                      
                      <Heading size="sm" mt={4} mb={2}>Sample Data (First 5 rows)</Heading>
                      <Box overflowX="auto">
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              {processedNamesResult.columns.map((column) => (
                                <Th key={column}>{column}</Th>
                              ))}
                            </Tr>
                          </Thead>
                          <Tbody>
                            {JSON.parse(processedNamesResult.sample_data).map((row, rowIndex) => (
                              <Tr key={rowIndex}>
                                {processedNamesResult.columns.map((column) => (
                                  <Td key={`${rowIndex}-${column}`}>{row[column]}</Td>
                                ))}
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                    </Box>
                  )}
                </>
              ) : (
                <Box p={4} bg="yellow.50" borderRadius="md">
                  <Text>Please import a file in Script 01 first, then return to this page.</Text>
                  <Button mt={2} colorScheme="blue" onClick={() => navigate('/script/1')}>
                    Go to Script 01
                  </Button>
                </Box>
              )}
            </VStack>
          ) : script.hasNameExclusion ? (
            <VStack spacing={4} align="stretch">
              {result ? (
                <>
                  <Box p={4} bg="blue.50" borderRadius="md">
                    <Text fontWeight="bold">Imported Data Summary:</Text>
                    <Text>Rows: {result.rows}</Text>
          <Text>Columns: {Array.isArray(result?.columns) ? result.columns.join(', ') : 'N/A'}</Text>
                  </Box>
                  
                  <FormControl>
                    <FormLabel>Exceptions (comma-separated)</FormLabel>
                    <Input
                      placeholder="e.g., Ana, Eva, Ida"
                      value={exceptions}
                      onChange={(e) => setExceptions(e.target.value)}
                    />
                    <FormHelperText>
                      Enter 3-letter names that should not be excluded, separated by commas
                    </FormHelperText>
                  </FormControl>
                  
                  <Box p={4} bg="gray.50" borderRadius="md">
                    <Text fontWeight="bold" mb={2}>Default Allowed Names:</Text>
                    <Text fontSize="sm" mb={2}>
                      These are the default 3-letter names that will not be excluded. You can modify the list above.
                    </Text>
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={2}>
                      <Box>
                        <Text fontWeight="semibold" mb={1}>Names:</Text>
                        <Text fontSize="sm">{defaultAllowedNames.join(', ')}</Text>
                      </Box>
                      <Box>
                        <Text fontWeight="semibold" mb={1}>Surnames:</Text>
                        <Text fontSize="sm">{defaultAllowedSurnames.join(', ')}</Text>
                      </Box>
                    </SimpleGrid>
                  </Box>
                  
                  <Button 
                    colorScheme="blue" 
                    onClick={handleExcludeNames} 
                    isLoading={isExcluding}
                    loadingText="Processing"
                  >
                    Exclude Names
                  </Button>
                  
                  {excludedResult && (
                    <Box mt={4}>
                      <Heading size="md" mb={2}>Excluded Results</Heading>
                      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={4}>
                        <Stat>
                          <StatLabel>Total Rows</StatLabel>
                          <StatNumber>{excludedResult.rows}</StatNumber>
                          <StatHelpText>After exclusion</StatHelpText>
                        </Stat>
                        <Stat>
                          <StatLabel>Excluded Names</StatLabel>
                          <StatNumber>{excludedResult.excluded_count}</StatNumber>
                          <StatHelpText>3-letter names removed</StatHelpText>
                        </Stat>
                        <Stat>
                          <StatLabel>Columns</StatLabel>
                          <StatNumber>{excludedResult.columns.length}</StatNumber>
                          <StatHelpText>Unchanged</StatHelpText>
                        </Stat>
                      </SimpleGrid>
                      
                      <Heading size="sm" mt={4} mb={2}>Sample Data (First 5 rows)</Heading>
                      <Box overflowX="auto">
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              {excludedResult.columns.map((column) => (
                                <Th key={column}>{column}</Th>
                              ))}
                            </Tr>
                          </Thead>
                          <Tbody>
                            {JSON.parse(excludedResult.sample_data).map((row, rowIndex) => (
                              <Tr key={rowIndex}>
                                {excludedResult.columns.map((column) => (
                                  <Td key={`${rowIndex}-${column}`}>{row[column]}</Td>
                                ))}
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                    </Box>
                  )}
                </>
              ) : (
                <Box p={4} bg="yellow.50" borderRadius="md">
                  <Text>Please import a file in Script 01 first, then return to this page.</Text>
                  <Button mt={2} colorScheme="blue" onClick={() => navigate('/script/1')}>
                    Go to Script 01
                  </Button>
                </Box>
              )}
            </VStack>
          ) : script.hasRecordLimiting ? (
            <VStack spacing={4} align="stretch">
              {result ? (
                <>
                  <Box p={4} bg="blue.50" borderRadius="md">
                    <Text fontWeight="bold">Imported Data Summary:</Text>
                    <Text>Rows: {result.rows}</Text>
          <Text>Columns: {Array.isArray(result?.columns) ? result.columns.join(', ') : 'N/A'}</Text>
                  </Box>
                  
                  <FormControl>
                    <FormLabel>Number of Records</FormLabel>
                    <NumberInput 
                      value={numRecords} 
                      onChange={(value) => setNumRecords(parseInt(value))}
                      min={1}
                      max={result.rows}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                    <FormHelperText>
                      Total number of records to keep after limiting
                    </FormHelperText>
                  </FormControl>
                  
                  <FormControl>
                    <FormLabel>Column to Limit By</FormLabel>
                    <Select
                      placeholder="Select a column"
                      value={selectedColumn}
                      onChange={(e) => setSelectedColumn(e.target.value)}
                    >
                      {result.columns.map((column) => (
                        <option key={column} value={column}>{column}</option>
                      ))}
                    </Select>
                    <FormHelperText>
                      Records will be limited based on values in this column
                    </FormHelperText>
                  </FormControl>
                  
                  <Button 
                    colorScheme="blue" 
                    onClick={handleLimitRecords} 
                    isLoading={isLimiting}
                    loadingText="Processing"
                  >
                    Limit Records
                  </Button>
                  
                  {limitedResult && (
                    <Box mt={4}>
                      <Heading size="md" mb={2}>Limited Results</Heading>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
                        <Stat>
                          <StatLabel>Total Rows</StatLabel>
                          <StatNumber>{limitedResult.rows}</StatNumber>
                          <StatHelpText>After limiting</StatHelpText>
                        </Stat>
                        <Stat>
                          <StatLabel>Columns</StatLabel>
                          <StatNumber>{limitedResult.columns.length}</StatNumber>
                          <StatHelpText>Unchanged</StatHelpText>
                        </Stat>
                      </SimpleGrid>
                      
                      <Heading size="sm" mt={4} mb={2}>Distribution by {selectedColumn}</Heading>
                      <Box overflowX="auto" mb={4}>
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              <Th>Value</Th>
                              <Th>Count</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {Object.entries(limitedResult.value_distribution).map(([value, count]) => (
                              <Tr key={value}>
                                <Td>{value}</Td>
                                <Td>{count}</Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                      
                      <Heading size="sm" mt={4} mb={2}>Sample Data (First 5 rows)</Heading>
                      <Box overflowX="auto">
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              {limitedResult.columns.map((column) => (
                                <Th key={column}>{column}</Th>
                              ))}
                            </Tr>
                          </Thead>
                          <Tbody>
                            {JSON.parse(limitedResult.sample_data).map((row, rowIndex) => (
                              <Tr key={rowIndex}>
                                {limitedResult.columns.map((column) => (
                                  <Td key={`${rowIndex}-${column}`}>{row[column]}</Td>
                                ))}
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                    </Box>
                  )}
                </>
              ) : (
                <Box p={4} bg="yellow.50" borderRadius="md">
                  <Text>Please import a file in Script 01 first, then return to this page.</Text>
                  <Button mt={2} colorScheme="blue" onClick={() => navigate('/script/1')}>
                    Go to Script 01
                  </Button>
                </Box>
              )}
            </VStack>
          ) : script.hasVolumeSelection ? (
            <VStack spacing={4} align="stretch">
              {result ? (
                <>
                  <Box p={4} bg="blue.50" borderRadius="md">
                    <Text fontWeight="bold">Imported Data Summary:</Text>
                    <Text>Rows: {result.rows}</Text>
                    <Text>Columns: {Array.isArray(result?.columns) ? result.columns.join(', ') : 'N/A'}</Text>
                  </Box>
                  
                  <FormControl>
                    <FormLabel>Target Volumes File (Excel)</FormLabel>
                    <Input 
                      type="file" 
                      accept=".xlsx" 
                      onChange={(e) => setTargetVolumesFile(e.target.files[0])}
                    />
                    <FormHelperText>
                      Upload an Excel file containing the target volumes
                    </FormHelperText>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Postal Code Column</FormLabel>
                    <Select
                      placeholder="Select postal code column"
                      value={cpColumn}
                      onChange={(e) => setCpColumn(e.target.value)}
                    >
                      {result.columns.map((column) => (
                        <option key={column} value={column}>{column}</option>
                      ))}
                    </Select>
                    <FormHelperText>
                      Column containing postal codes
                    </FormHelperText>
                  </FormControl>

                  {scriptId === '7.3' && (
                    <FormControl>
                      <FormLabel>Age Column</FormLabel>
                      <Select
                        placeholder="Select age column"
                        value={ageColumn}
                        onChange={(e) => setAgeColumn(e.target.value)}
                      >
                        {result.columns.map((column) => (
                          <option key={column} value={column}>{column}</option>
                        ))}
                      </Select>
                      <FormHelperText>
                        Column containing age values
                      </FormHelperText>
                    </FormControl>
                  )}
                  
                  <Button 
                    colorScheme="blue" 
                    onClick={handleSelectVolume} 
                    isLoading={isSelectingVolume}
                    loadingText="Processing"
                  >
                    Select Volume
                  </Button>
                  
                  {selectedVolumeResult && (
                    <Box mt={4}>
                      <Heading size="md" mb={2}>Selected Volume Results</Heading>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
                        <Stat>
                          <StatLabel>Total Rows</StatLabel>
                          <StatNumber>{selectedVolumeResult.rows}</StatNumber>
                          <StatHelpText>After selection</StatHelpText>
                        </Stat>
                        <Stat>
                          <StatLabel>Columns</StatLabel>
                          <StatNumber>{selectedVolumeResult.columns.length}</StatNumber>
                          <StatHelpText>Unchanged</StatHelpText>
                        </Stat>
                      </SimpleGrid>
                      
                      <Heading size="sm" mt={4} mb={2}>Distribution by Postal Code</Heading>
                      <Box overflowX="auto" mb={4}>
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              <Th>Postal Code</Th>
                              <Th>Count</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {Object.entries(selectedVolumeResult.value_distribution).map(([cp, count]) => (
                              <Tr key={cp}>
                                <Td>{cp}</Td>
                                <Td>{count}</Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                      
                      <Heading size="sm" mt={4} mb={2}>Sample Data (First 5 rows)</Heading>
                      <Box overflowX="auto">
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              {selectedVolumeResult.columns.map((column) => (
                                <Th key={column}>{column}</Th>
                              ))}
                            </Tr>
                          </Thead>
                          <Tbody>
                            {JSON.parse(selectedVolumeResult.sample_data).map((row, rowIndex) => (
                              <Tr key={rowIndex}>
                                {selectedVolumeResult.columns.map((column) => (
                                  <Td key={`${rowIndex}-${column}`}>{row[column]}</Td>
                                ))}
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                    </Box>
                  )}
                </>
              ) : (
                <Box p={4} bg="yellow.50" borderRadius="md">
                  <Text>Please import a file in Script 01 first, then return to this page.</Text>
                  <Button mt={2} colorScheme="blue" onClick={() => navigate('/script/1')}>
                    Go to Script 01
                  </Button>
                </Box>
              )}
            </VStack>
          ) : script.hasDichotomousVariables ? (
            <VStack spacing={4} align="stretch">
              {result ? (
                <>
                  <Box p={4} bg="blue.50" borderRadius="md">
                    <Text fontWeight="bold">Imported Data Summary:</Text>
                    <Text>Rows: {result.rows}</Text>
                    <Text>Columns: {Array.isArray(result?.columns) ? result.columns.join(', ') : 'N/A'}</Text>
                  </Box>
                  
                  <FormControl>
                    <FormLabel>Column to Create Dichotomous Variables From</FormLabel>
                    <Select
                      placeholder="Select a column"
                      value={dichotomousColumn}
                      onChange={(e) => setDichotomousColumn(e.target.value)}
                    >
                      {result.columns.map((column) => (
                        <option key={column} value={column}>{column}</option>
                      ))}
                    </Select>
                    <FormHelperText>
                      Select the column to create dichotomous variables from
                    </FormHelperText>
                  </FormControl>
                  
                  <FormControl>
                    <FormLabel>Prefix for New Columns (Optional)</FormLabel>
                    <Input
                      placeholder="e.g., is_"
                      value={dichotomousPrefix}
                      onChange={(e) => setDichotomousPrefix(e.target.value)}
                    />
                    <FormHelperText>
                      Prefix to add to the new column names
                    </FormHelperText>
                  </FormControl>
                  
                  <Box p={4} bg="gray.50" borderRadius="md">
                    <Heading size="sm" mb={2}>Value and Operator Pairs</Heading>
                    <HStack spacing={4} mb={4} align="flex-end">
                      <FormControl flex="1">
                        <FormLabel>Value</FormLabel>
                        <NumberInput
                          value={newValue}
                          onChange={(value) => setNewValue(value)}
                        >
                          <NumberInputField placeholder="Enter a number" />
                          <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                          </NumberInputStepper>
                        </NumberInput>
                      </FormControl>
                      
                      <FormControl flex="1">
                        <FormLabel>Operator</FormLabel>
                        <Select
                          value={newOperator}
                          onChange={(e) => setNewOperator(e.target.value)}
                        >
                          <option value="eq">Equal (=)</option>
                          <option value="gt">Greater Than (&gt;)</option>
                          <option value="gte">Greater Than or Equal (≥)</option>
                          <option value="lt">Less Than (&lt;)</option>
                          <option value="lte">Less Than or Equal (≤)</option>
                        </Select>
                      </FormControl>
                      
                      <Button
                        colorScheme="blue"
                        onClick={handleAddDichotomousValue}
                      >
                        Add Pair
                      </Button>
                    </HStack>
                    
                    {dichotomousValues.length > 0 ? (
                      <TableContainer>
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              <Th>Value</Th>
                              <Th>Operator</Th>
                              <Th>Action</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {dichotomousValues.map((pair, index) => (
                              <Tr key={index}>
                                <Td>{pair.value}</Td>
                                <Td>
                                  {operatorSymbols[pair.operator]}
                                </Td>
                                <Td>
                                  <Button
                                    size="xs"
                                    colorScheme="red"
                                    onClick={() => handleRemoveDichotomousValue(index)}
                                  >
                                    Remove
                                  </Button>
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Text color="gray.500">No value pairs added yet</Text>
                    )}
                  </Box>
                  
                  <Button 
                    colorScheme="blue" 
                    onClick={handleCreateDichotomous} 
                    isLoading={isCreatingDichotomous}
                    loadingText="Processing"
                  >
                    Create Dichotomous Variables
                  </Button>
                  
                  {dichotomousResult && (
                    <Box mt={4}>
                      <Heading size="md" mb={2}>Dichotomous Variables Results</Heading>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
                        <Stat>
                          <StatLabel>Total Rows</StatLabel>
                          <StatNumber>{dichotomousResult.rows}</StatNumber>
                          <StatHelpText>Unchanged</StatHelpText>
                        </Stat>
                        <Stat>
                          <StatLabel>Columns</StatLabel>
                          <StatNumber>{dichotomousResult.columns.length}</StatNumber>
                          <StatHelpText>Including new dichotomous columns</StatHelpText>
                        </Stat>
                      </SimpleGrid>
                      
                      <Heading size="sm" mt={4} mb={2}>Sample Data (First 5 rows)</Heading>
                      <Box overflowX="auto">
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              {dichotomousResult.columns.map((column) => (
                                <Th key={column}>{column}</Th>
                              ))}
                            </Tr>
                          </Thead>
                          <Tbody>
                            {JSON.parse(dichotomousResult.sample_data).map((row, rowIndex) => (
                              <Tr key={rowIndex}>
                                {dichotomousResult.columns.map((column) => (
                                  <Td key={`${rowIndex}-${column}`}>{row[column]}</Td>
                                ))}
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                    </Box>
                  )}
                </>
              ) : (
                <Box p={4} bg="yellow.50" borderRadius="md">
                  <Text>Please import a file in Script 01 first, then return to this page.</Text>
                  <Button mt={2} colorScheme="blue" onClick={() => navigate('/script/1')}>
                    Go to Script 01
                  </Button>
                </Box>
              )}
            </VStack>
          ) : script.hasColumnDropping ? (
            <VStack spacing={4} align="stretch">
              {result ? (
                <>
                  <Box p={4} bg="blue.50" borderRadius="md">
                    <Text fontWeight="bold">Imported Data Summary:</Text>
                    <Text>Rows: {result.rows}</Text>
          <Text>Columns: {Array.isArray(result?.columns) ? result.columns.join(', ') : 'N/A'}</Text>
                  </Box>
                  
                  <FormControl>
                    <FormLabel>Column to Drop</FormLabel>
                    <Select
                      placeholder="Select a column to drop"
                      value={columnToDrop}
                      onChange={(e) => setColumnToDrop(e.target.value)}
                    >
                      {result.columns.map((column) => (
                        <option key={column} value={column}>{column}</option>
                      ))}
                    </Select>
                    <FormHelperText>
                      Select the column you want to remove from the dataset
                    </FormHelperText>
                  </FormControl>
                  
                  <Button 
                    colorScheme="red" 
                    onClick={handleDropColumn} 
                    isLoading={isDroppingColumn}
                    loadingText="Processing"
                  >
                    Drop Column
                  </Button>
                  
                  {droppedResult && (
                    <Box mt={4}>
                      <Heading size="md" mb={2}>Column Dropped Results</Heading>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
                        <Stat>
                          <StatLabel>Total Rows</StatLabel>
                          <StatNumber>{droppedResult.rows}</StatNumber>
                          <StatHelpText>Unchanged</StatHelpText>
                        </Stat>
                        <Stat>
                          <StatLabel>Columns</StatLabel>
                          <StatNumber>{droppedResult.columns.length}</StatNumber>
                          <StatHelpText>After dropping '{droppedResult.dropped_column}'</StatHelpText>
                        </Stat>
                      </SimpleGrid>
                      
                      <Heading size="sm" mt={4} mb={2}>Sample Data (First 5 rows)</Heading>
                      <Box overflowX="auto">
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              {droppedResult.columns.map((column) => (
                                <Th key={column}>{column}</Th>
                              ))}
                            </Tr>
                          </Thead>
                          <Tbody>
                            {JSON.parse(droppedResult.sample_data).map((row, rowIndex) => (
                              <Tr key={rowIndex}>
                                {droppedResult.columns.map((column) => (
                                  <Td key={`${rowIndex}-${column}`}>{row[column]}</Td>
                                ))}
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                    </Box>
                  )}
                </>
              ) : (
                <Box p={4} bg="yellow.50" borderRadius="md">
                  <Text>Please import a file in Script 01 first, then return to this page.</Text>
                  <Button mt={2} colorScheme="blue" onClick={() => navigate('/script/1')}>
                    Go to Script 01
                  </Button>
                </Box>
              )}
            </VStack>
          ) : script.hasDeduplication ? (
            <VStack spacing={4} align="stretch">
              {result ? (
                <>
                  <Box p={4} bg="blue.50" borderRadius="md">
                    <Text fontWeight="bold">Imported Data Summary:</Text>
                    <Text>Rows: {result.rows}</Text>
          <Text>Columns: {Array.isArray(result?.columns) ? result.columns.join(', ') : 'N/A'}</Text>
                  </Box>
                  
                  <Box p={4} bg="gray.50" borderRadius="md">
                    <Heading size="sm" mb={2}>Client Database Files</Heading>
                    <Text mb={4}>Add client database files to deduplicate against</Text>
                    
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
                      <FormControl>
                        <FormLabel>File Type</FormLabel>
                        <RadioGroup value={fileType} onChange={setFileType}>
                          <Radio value="xlsx">Excel (.xlsx)</Radio>
                          <Radio value="csv">CSV (.csv)</Radio>
                        </RadioGroup>
                      </FormControl>
                      
                      <FormControl>
                        <FormLabel>Select File</FormLabel>
                        <Input 
                          type="file" 
                          accept={fileType === 'xlsx' ? '.xlsx' : '.csv'} 
                          onChange={handleFileChange}
                        />
                      </FormControl>
                    </SimpleGrid>
                    
                    <Button 
                      colorScheme="blue" 
                      onClick={handleAddClientFile}
                      mb={4}
                    >
                      Add Client File
                    </Button>
                    
                    {clientFiles.length > 0 ? (
                      <Box overflowX="auto">
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              <Th>Filename</Th>
                              <Th>Type</Th>
                              <Th>Action</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {clientFiles.map((file, index) => (
                              <Tr key={index}>
                                <Td>{file.filename}</Td>
                                <Td>{file.file_type}</Td>
                                <Td>
                                  <Button 
                                    size="xs" 
                                    colorScheme="red" 
                                    onClick={() => handleRemoveClientFile(index)}
                                  >
                                    Remove
                                  </Button>
                                </Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                    ) : (
                      <Text color="gray.500">No client files added yet</Text>
                    )}
                  </Box>
                  
                  <Button 
                    colorScheme="blue" 
                    onClick={handleDeduplicate} 
                    isLoading={isDeduplicating}
                    loadingText="Deduplicating"
                  >
                    Deduplicate
                  </Button>
                  
                  {deduplicationResult && (
                    <Box mt={4}>
                      <Heading size="md" mb={2}>Deduplication Results</Heading>
                      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={4}>
                        <Stat>
                          <StatLabel>Total Rows</StatLabel>
                          <StatNumber>{deduplicationResult.rows}</StatNumber>
                          <StatHelpText>After deduplication</StatHelpText>
                        </Stat>
                        <Stat>
                          <StatLabel>Removed Records</StatLabel>
                          <StatNumber>{deduplicationResult.removed_records}</StatNumber>
                          <StatHelpText>Records removed</StatHelpText>
                        </Stat>
                        <Stat>
                          <StatLabel>Columns</StatLabel>
                          <StatNumber>{deduplicationResult.columns.length}</StatNumber>
                          <StatHelpText>Unchanged</StatHelpText>
                        </Stat>
                      </SimpleGrid>
                      
                      <Heading size="sm" mt={4} mb={2}>Sample Data (First 5 rows)</Heading>
                      <Box overflowX="auto">
                        <Table variant="simple" size="sm">
                          <Thead>
                            <Tr>
                              {deduplicationResult.columns.map((column) => (
                                <Th key={column}>{column}</Th>
                              ))}
                            </Tr>
                          </Thead>
                          <Tbody>
                            {JSON.parse(deduplicationResult.sample_data).map((row, rowIndex) => (
                              <Tr key={rowIndex}>
                                {deduplicationResult.columns.map((column) => (
                                  <Td key={`${rowIndex}-${column}`}>{row[column]}</Td>
                                ))}
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </Box>
                    </Box>
                  )}
                </>
              ) : (
                <Box p={4} bg="yellow.50" borderRadius="md">
                  <Text>Please import a file in Script 01 first, then return to this page.</Text>
                  <Button mt={2} colorScheme="blue" onClick={() => navigate('/script/1')}>
                    Go to Script 01
                  </Button>
                </Box>
              )}
            </VStack>
          ) : script.hasExport ? (
            <VStack spacing={4} align="stretch">
              {result ? (
                <>
                  <Box p={4} bg="blue.50" borderRadius="md">
                    <Text fontWeight="bold">Data to Export Summary:</Text>
                    <Text>Rows: {result.rows}</Text>
          <Text>Columns: {Array.isArray(result?.columns) ? result.columns.join(', ') : 'N/A'}</Text>
                  </Box>
                  
                  <FormControl>
                    <FormLabel>Export Format</FormLabel>
                    <Select
                      placeholder="Select export format"
                      value={exportFormat}
                      onChange={(e) => setExportFormat(e.target.value)}
                    >
                      <option value="csv">CSV (.csv)</option>
                      <option value="xlsx">Excel (.xlsx)</option>
                      <option value="json">JSON (.json)</option>
                    </Select>
                    <FormHelperText>
                      Select the format to export the data to
                    </FormHelperText>
                  </FormControl>
                  
                  <FormControl>
                    <FormLabel>Filename</FormLabel>
                    <Input
                      placeholder="e.g., exported_data"
                      value={exportFilename}
                      onChange={(e) => setExportFilename(e.target.value)}
                    />
                    <FormHelperText>
                      Enter the name for the exported file (without extension)
                    </FormHelperText>
                  </FormControl>

                  <FormControl>
                    <Checkbox
                      isChecked={sendEmail}
                      onChange={(e) => setSendEmail(e.target.checked)}
                    >
                      Send via email
                    </Checkbox>
                  </FormControl>

                  {sendEmail && (
                    <FormControl>
                      <FormLabel>Email Address</FormLabel>
                      <Input
                        type="email"
                        placeholder="Enter your email address"
                        value={exportEmail}
                        onChange={(e) => setExportEmail(e.target.value)}
                      />
                      <FormHelperText>
                        The exported file will be sent to this email address
                      </FormHelperText>
                    </FormControl>
                  )}
                  
                  <Button 
                    colorScheme="blue" 
                    onClick={handleExport} 
                    isLoading={isExporting}
                    loadingText="Exporting"
                  >
                    Export Data
                  </Button>
                  
                  {exportResult && (
                    <Box mt={4} p={4} bg="green.50" borderRadius="md">
                      <Heading size="sm" mb={2}>Export Successful</Heading>
                      <Text>File: {exportResult.filename}</Text>
                      <Text>Format: {exportResult.content_type}</Text>
                      {!sendEmail && (
                        <Text mt={2}>The file has been downloaded to your computer.</Text>
                      )}
                    </Box>
                  )}
                </>
              ) : (
                <Box p={4} bg="yellow.50" borderRadius="md">
                  <Text>Please import a file in Script 01 first, then return to this page.</Text>
                  <Button mt={2} colorScheme="blue" onClick={() => navigate('/script/1')}>
                    Go to Script 01
                  </Button>
                </Box>
              )}
            </VStack>
          ) : script.hasConcatenation ? (
            <VStack spacing={4} align="stretch">
              <Box p={4} bg="blue.50" borderRadius="md">
                <Heading size="sm" mb={2}>Concatenar Archivos</Heading>
                <Text mb={4}>Selecciona los archivos que deseas concatenar</Text>
                
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
                  <FormControl>
                    <FormLabel>File Type</FormLabel>
                    <RadioGroup value={fileType} onChange={setFileType}>
                      <Radio value="xlsx">Excel (.xlsx)</Radio>
                      <Radio value="csv">CSV (.csv)</Radio>
                    </RadioGroup>
                  </FormControl>
                  
                  <FormControl>
                    <FormLabel>Select File</FormLabel>
                    <Input 
                      type="file" 
                      accept={fileType === 'xlsx' ? '.xlsx' : '.csv'} 
                      onChange={handleAddFileToConcatenate}
                    />
                  </FormControl>
                </SimpleGrid>
              </Box>

              {filesToConcatenate.length > 0 && (
                <Box p={4} bg="gray.50" borderRadius="md">
                  <Heading size="sm" mb={2}>Archivos a concatenar</Heading>
                  <TableContainer>
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>Nombre del archivo</Th>
                          <Th>Tipo</Th>
                          <Th>Acciones</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {filesToConcatenate.map((file, index) => (
                          <Tr key={index}>
                            <Td>{file.name}</Td>
                            <Td>{file.file_type}</Td>
                            <Td>
                              <Button
                                size="xs"
                                colorScheme="red"
                                onClick={() => handleRemoveFileFromConcatenate(index)}
                              >
                                Eliminar
                              </Button>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              <Button
                colorScheme="blue"
                onClick={handleConcatenateFiles}
                isLoading={isConcatenating}
                loadingText="Concatenando..."
                isDisabled={filesToConcatenate.length < 2}
              >
                Concatenar Archivos
              </Button>

              {concatenationResult && (
                <Box mt={4}>
                  <Heading size="md" mb={2}>Resultados de la concatenación</Heading>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
                    <Stat>
                      <StatLabel>Total de filas</StatLabel>
                      <StatNumber>{concatenationResult.total_rows}</StatNumber>
                      <StatHelpText>Después de la concatenación</StatHelpText>
                    </Stat>
                    <Stat>
                      <StatLabel>Archivos concatenados</StatLabel>
                      <StatNumber>{concatenationResult.total_files}</StatNumber>
                      <StatHelpText>Total de archivos procesados</StatHelpText>
                    </Stat>
                  </SimpleGrid>

                  <Box p={4} bg="blue.50" borderRadius="md" mb={4}>
                    <Text fontWeight="bold">Columnas:</Text>
                    <Text>{Array.isArray(concatenationResult?.columns) ? concatenationResult.columns.join(', ') : 'N/A'}</Text>
                  </Box>

                  <Heading size="sm" mt={4} mb={2}>Muestra de datos (Primeras 5 filas)</Heading>
                  <Box overflowX="auto">
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          {concatenationResult.columns.map((column) => (
                            <Th key={column}>{column}</Th>
                          ))}
                        </Tr>
                      </Thead>
                      <Tbody>
                        {JSON.parse(concatenationResult.sample_data).map((row, index) => (
                          <Tr key={index}>
                            {concatenationResult.columns.map((column) => (
                              <Td key={column}>{row[column]}</Td>
                            ))}
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </Box>
              )}
            </VStack>
          ) : script.hasNominatim ? (
            <VStack spacing={4} align="stretch">
              {(() => {
                // Check for imported data in localStorage
                const importedData = localStorage.getItem('importedDataFrame');
                const data = importedData ? JSON.parse(importedData) : null;

                if (!data) {
                  return (
                    <Box p={4} bg="yellow.50" borderRadius="md">
                      <Text>Please import a file in Script 01 first, then return to this page.</Text>
                      <Button mt={2} colorScheme="blue" onClick={() => navigate('/script/1')}>
                        Go to Script 01
                      </Button>
                    </Box>
                  );
                }

                return (
                  <>
                    <Box p={4} bg="blue.50" borderRadius="md">
                      <Text fontWeight="bold">Imported Data Summary:</Text>
                      <Text>Rows: {data.rows}</Text>
                      <Text>Columns: {Array.isArray(data?.columns) ? data.columns.join(', ') : 'N/A'}</Text>
                    </Box>

                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      <FormControl>
                        <FormLabel>Address Column</FormLabel>
                        <Select
                          value={direccionColumn}
                          onChange={(e) => setDireccionColumn(e.target.value)}
                          placeholder="Select address column"
                        >
                          {data.columns.map((column) => (
                            <option key={column} value={column}>
                              {column}
                            </option>
                          ))}
                        </Select>
                      </FormControl>

                      <FormControl>
                        <FormLabel>Postal Code Column</FormLabel>
                        <Select
                          value={cpColumn}
                          onChange={(e) => setCpColumn(e.target.value)}
                          placeholder="Select postal code column"
                        >
                          {data.columns.map((column) => (
                            <option key={column} value={column}>
                              {column}
                            </option>
                          ))}
                        </Select>
                      </FormControl>
                    </SimpleGrid>

                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                      <FormControl>
                        <FormLabel>Municipio Output Column</FormLabel>
                        <Input
                          value={municipioOut}
                          onChange={(e) => setMunicipioOut(e.target.value)}
                          placeholder="municipio"
                        />
                      </FormControl>

                      <FormControl>
                        <FormLabel>Street Output Column</FormLabel>
                        <Input
                          value={calleOut}
                          onChange={(e) => setCalleOut(e.target.value)}
                          placeholder="calle"
                        />
                      </FormControl>

                      <FormControl>
                        <FormLabel>Road Type Output Column</FormLabel>
                        <Input
                          value={tipoViaOut}
                          onChange={(e) => setTipoViaOut(e.target.value)}
                          placeholder="tipo_via"
                        />
                      </FormControl>
                    </SimpleGrid>

                    <FormControl>
                      <FormLabel>Email</FormLabel>
                      <Input
                        type="email"
                        value={nominatimEmail}
                        onChange={(e) => setNominatimEmail(e.target.value)}
                        placeholder="Enter your email"
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel>Output File Format</FormLabel>
                      <RadioGroup value={fileExtension} onChange={setFileExtension}>
                        <HStack spacing={4}>
                          <Radio value="csv">CSV</Radio>
                          <Radio value="xlsx">XLSX</Radio>
                        </HStack>
                      </RadioGroup>
                    </FormControl>

                    <Button
                      colorScheme="blue"
                      onClick={handleNominatimAddresses}
                      isLoading={isProcessingNominatim}
                      loadingText="Processing"
                      isDisabled={!direccionColumn || !cpColumn || !nominatimEmail}
                    >
                      Process Addresses
                    </Button>

                    {nominatimLogs.length > 0 && (
                      <Box mt={4}>
                        <Heading size="sm" mb={2}>Processing Logs</Heading>
                        <Box maxH="200px" overflowY="auto" p={2} bg="gray.50" borderRadius="md">
                          {nominatimLogs.map((log, index) => (
                            <Text key={index} fontSize="sm" mb={1}>
                              {log}
                            </Text>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </>
                );
              })()}
            </VStack>
          ) : script.hasCountByGroups ? (
            <VStack spacing={4} align="stretch">
              {result ? (
                <>
                  <Box p={4} bg="blue.50" borderRadius="md">
                    <Text fontWeight="bold">Imported Data Summary:</Text>
                    <Text>Rows: {result.rows}</Text>
                    <Text>Columns: {Array.isArray(result?.columns) ? result.columns.join(', ') : 'N/A'}</Text>
                  </Box>
                  
                  <FormControl>
                    <FormLabel>Group By</FormLabel>
                    <Select value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
                      <option value="genero">Gender</option>
                      <option value="edad">Age</option>
                    </Select>
                  </FormControl>

                  {groupBy === 'edad' && (
                    <Box mb={4}>
                      <Heading size="sm" mb={2}>Age Ranges</Heading>
                      {ageRanges.length === 0 ? (
                        <Text color="gray.500" mb={4}>No age ranges defined. Click "Add Range" to create one.</Text>
                      ) : (
                        ageRanges.map((range, index) => (
                          <Flex key={index} mb={2}>
                            <NumberInput
                              value={range.min}
                              onChange={(value) => {
                                const newRanges = [...ageRanges];
                                newRanges[index].min = parseInt(value);
                                setAgeRanges(newRanges);
                              }}
                              min={0}
                              max={range.max - 1}
                              mr={2}
                            >
                              <NumberInputField />
                            </NumberInput>
                            <NumberInput
                              value={range.max === Infinity ? '' : range.max}
                              onChange={(value) => {
                                const newRanges = [...ageRanges];
                                newRanges[index].max = value === '' ? Infinity : parseInt(value);
                                setAgeRanges(newRanges);
                              }}
                              min={range.min + 1}
                              mr={2}
                            >
                              <NumberInputField />
                            </NumberInput>
                            <Input
                              value={range.label}
                              onChange={(e) => {
                                const newRanges = [...ageRanges];
                                newRanges[index].label = e.target.value;
                                setAgeRanges(newRanges);
                              }}
                              placeholder="Label"
                              mr={2}
                            />
                            <IconButton
                              icon={<DeleteIcon />}
                              onClick={() => {
                                setAgeRanges(ageRanges.filter((_, i) => i !== index));
                              }}
                              aria-label="Remove range"
                            />
                          </Flex>
                        ))
                      )}
                      <Button
                        leftIcon={<AddIcon />}
                        onClick={() => {
                          const lastRange = ageRanges[ageRanges.length - 1];
                          const newMin = lastRange ? lastRange.max : 18;
                          setAgeRanges([...ageRanges, { min: newMin, max: newMin + 10, label: '' }]);
                        }}
                        size="sm"
                        mt={2}
                      >
                        Add Range
                      </Button>
                    </Box>
                  )}

                  <Button
                    colorScheme="blue"
                    onClick={handleCountByGroups}
                    isLoading={isCounting}
                    loadingText="Counting..."
                    mb={4}
                  >
                    Count by Groups
                  </Button>

                  {countResult && (
                    <Box>
                      <Heading size="sm" mb={2}>Results</Heading>
                      <Text>Total Records: {countResult.total}</Text>
                      <Text mb={4}>Grouped by: {countResult.group_by === 'genero' ? 'Gender' : 'Age'}</Text>
                      
                      <TableContainer>
                        <Table variant="simple">
                          <Thead>
                            <Tr>
                              <Th>Group</Th>
                              <Th isNumeric>Count</Th>
                              <Th isNumeric>Percentage</Th>
                            </Tr>
                          </Thead>
                          <Tbody>
                            {Object.entries(countResult.counts).map(([group, count]) => (
                              <Tr key={group}>
                                <Td>{group}</Td>
                                <Td isNumeric>{count}</Td>
                                <Td isNumeric>{countResult.percentages[group].toFixed(2)}%</Td>
                              </Tr>
                            ))}
                          </Tbody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
                </>
              ) : (
                <Box p={4} bg="yellow.50" borderRadius="md">
                  <Text>Please import a file in Script 01 first, then return to this page.</Text>
                  <Button mt={2} colorScheme="blue" onClick={() => navigate('/script/1')}>
                    Go to Script 01
                  </Button>
                </Box>
              )}
            </VStack>
          ) : script.hasSplitDataframe ? (
            <VStack spacing={4} align="stretch">
              {result ? (
                <>
                  <Box p={4} bg="blue.50" borderRadius="md">
                    <Text fontWeight="bold">Imported Data Summary:</Text>
                    <Text>Rows: {result.rows}</Text>
                    <Text>Columns: {Array.isArray(result?.columns) ? result.columns.join(', ') : 'N/A'}</Text>
                  </Box>
                  
                  <FormControl>
                    <FormLabel>Maximum Rows Per File</FormLabel>
                    <NumberInput 
                      value={maxRowsPerFile} 
                      onChange={(value) => setMaxRowsPerFile(parseInt(value))}
                      min={1}
                      max={result.rows}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                    <FormHelperText>
                      Maximum number of rows each split file should contain
                    </FormHelperText>
                  </FormControl>
                  
                  <FormControl>
                    <FormLabel>Output Format</FormLabel>
                    <Select
                      value={outputFormat}
                      onChange={(e) => setOutputFormat(e.target.value)}
                    >
                      <option value="xlsx">Excel (.xlsx)</option>
                      <option value="csv">CSV (.csv)</option>
                    </Select>
                    <FormHelperText>
                      Format for the split files
                    </FormHelperText>
                  </FormControl>
                  
                  <Button 
                    colorScheme="blue" 
                    onClick={handleSplitDataframe} 
                    isLoading={isSplitting}
                    loadingText="Splitting"
                  >
                    Split DataFrame
                  </Button>
                  
                  {splitResult && (
                    <Box mt={4}>
                      <Heading size="md" mb={2}>Split Results</Heading>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
                        <Stat>
                          <StatLabel>Total Files</StatLabel>
                          <StatNumber>{splitResult.total_files}</StatNumber>
                          <StatHelpText>Files created</StatHelpText>
                        </Stat>
                        <Stat>
                          <StatLabel>Rows Per File</StatLabel>
                          <StatNumber>{maxRowsPerFile}</StatNumber>
                          <StatHelpText>Maximum rows per file</StatHelpText>
                        </Stat>
                      </SimpleGrid>
                      
                      <Box p={4} bg="green.50" borderRadius="md">
                        <Heading size="sm" mb={2}>Files Created:</Heading>
                        <VStack align="stretch" spacing={2}>
                          {splitResult.files.map((file, index) => (
                            <Box key={index} p={2} bg="white" borderRadius="md">
                              <Text fontWeight="bold">{file.filename}</Text>
                              <Text>Rows: {file.rows}</Text>
                              <Text>Format: {file.format}</Text>
                            </Box>
                          ))}
                        </VStack>
                      </Box>
                    </Box>
                  )}
                </>
              ) : (
                <Box p={4} bg="yellow.50" borderRadius="md">
                  <Text>Please import a file in Script 01 first, then return to this page.</Text>
                  <Button mt={2} colorScheme="blue" onClick={() => navigate('/script/1')}>
                    Go to Script 01
                  </Button>
                </Box>
              )}
            </VStack>
          ) : script.hasConcatenation ? (
            <VStack spacing={4} align="stretch">
              <Box p={4} bg="blue.50" borderRadius="md">
                <Heading size="sm" mb={2}>Concatenar Archivos</Heading>
                <Text mb={4}>Selecciona los archivos que deseas concatenar</Text>
                
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
                  <FormControl>
                    <FormLabel>File Type</FormLabel>
                    <RadioGroup value={fileType} onChange={setFileType}>
                      <Radio value="xlsx">Excel (.xlsx)</Radio>
                      <Radio value="csv">CSV (.csv)</Radio>
                    </RadioGroup>
                  </FormControl>
                  
                  <FormControl>
                    <FormLabel>Select File</FormLabel>
                    <Input 
                      type="file" 
                      accept={fileType === 'xlsx' ? '.xlsx' : '.csv'} 
                      onChange={handleAddFileToConcatenate}
                    />
                  </FormControl>
                </SimpleGrid>
              </Box>

              {filesToConcatenate.length > 0 && (
                <Box p={4} bg="gray.50" borderRadius="md">
                  <Heading size="sm" mb={2}>Archivos a concatenar</Heading>
                  <TableContainer>
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          <Th>Nombre del archivo</Th>
                          <Th>Tipo</Th>
                          <Th>Acciones</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {filesToConcatenate.map((file, index) => (
                          <Tr key={index}>
                            <Td>{file.name}</Td>
                            <Td>{file.file_type}</Td>
                            <Td>
                              <Button
                                size="xs"
                                colorScheme="red"
                                onClick={() => handleRemoveFileFromConcatenate(index)}
                              >
                                Eliminar
                              </Button>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              <Button
                colorScheme="blue"
                onClick={handleConcatenateFiles}
                isLoading={isConcatenating}
                loadingText="Concatenando..."
                isDisabled={filesToConcatenate.length < 2}
              >
                Concatenar Archivos
              </Button>

              {concatenationResult && (
                <Box mt={4}>
                  <Heading size="md" mb={2}>Resultados de la concatenación</Heading>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
                    <Stat>
                      <StatLabel>Total de filas</StatLabel>
                      <StatNumber>{concatenationResult.total_rows}</StatNumber>
                      <StatHelpText>Después de la concatenación</StatHelpText>
                    </Stat>
                    <Stat>
                      <StatLabel>Archivos concatenados</StatLabel>
                      <StatNumber>{concatenationResult.total_files}</StatNumber>
                      <StatHelpText>Total de archivos procesados</StatHelpText>
                    </Stat>
                  </SimpleGrid>

                  <Box p={4} bg="blue.50" borderRadius="md" mb={4}>
                    <Text fontWeight="bold">Columnas:</Text>
                    <Text>{Array.isArray(concatenationResult?.columns) ? concatenationResult.columns.join(', ') : 'N/A'}</Text>
                  </Box>

                  <Heading size="sm" mt={4} mb={2}>Muestra de datos (Primeras 5 filas)</Heading>
                  <Box overflowX="auto">
                    <Table variant="simple" size="sm">
                      <Thead>
                        <Tr>
                          {concatenationResult.columns.map((column) => (
                            <Th key={column}>{column}</Th>
                          ))}
                        </Tr>
                      </Thead>
                      <Tbody>
                        {JSON.parse(concatenationResult.sample_data).map((row, index) => (
                          <Tr key={index}>
                            {concatenationResult.columns.map((column) => (
                              <Td key={column}>{row[column]}</Td>
                            ))}
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </Box>
              )}
            </VStack>
          ) : null}
        </Box>
        
        {scriptId !== '10' && scriptId !== '13' && scriptId !== '16' && scriptId !== '17' && (
          <Button 
            colorScheme="green" 
            onClick={() => navigate('/script/10')}
            mb={4}
          >
            Export Results
          </Button>
        )}
        
        <Button onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </Button>
      </VStack>
    </Container>
  );
};

export default ScriptPage; 